import { useState, useEffect, useRef } from 'react';
import './App.css';
import { migrateLegacyData, masterScope, seedTemplates, scopedKey, ensureSystemBaseTemplates, ensureSystemDefaultMessages, pushReminder, propagateReminder } from './scope';
import { ensureTenancy, loadOrgs, saveOrgs, orgById, seatsUsed, genId, systemScope, orgAgents, orgInTrial, orgTrialDaysLeft, orgEffectiveSeats, orgBlockedReason, orgOverCapacity, orgSubscribed, ensureSystemOrgs, systemOrgForMls, userExpired, userDaysLeft, userPaid, processHaltReason, effectiveTrialDays } from './tenancy';
import { passwordError } from './password';
import ReminderTemplates from './ReminderTemplates';
import ReminderAssignments from './ReminderAssignments';
import Contacts from './Contacts';
import Properties from './Properties';
import GettingStarted from './GettingStarted';
import { setupProgress } from './setupProgress';
import { resizeImage } from './image';
import SetupBanner from './SetupBanner';
import RunReminders from './RunReminders';
import Login from './Login';
import Landing from './Landing';
import AgentLanding from './AgentLanding';
import ReviewSubmit from './ReviewSubmit';
import Reviews from './Reviews';
import { useI18n } from './i18n';
import Profile from './Profile';
import Admin from './Admin';
import SupportedMls from './SupportedMls';
import Process from './Process';
import ServerRunStatus from './ServerRunStatus';
import SystemAdmin from './SystemAdmin';
import SystemSettings from './SystemSettings';
import FilesLibrary from './FilesLibrary';
import FileManager from './FileManager';
import SyncStatus from './SyncStatus';
import MessageAgents from './MessageAgents';
import InviteAgents from './InviteAgents';
import MlsSettings from './MlsSettings';
import MessageContacts from './MessageContacts';
import ForcePasswordChange from './ForcePasswordChange';
import ResetPassword from './ResetPassword';
import { api, usingApi } from './api';
import { authStore } from './store/authStore';
import { mlsStore } from './store/mlsStore';
import { orgStore } from './store/orgStore';
import { docStore, isMirroredKey } from './store/docStore';
import * as syncState from './store/syncState';
import Icon from './Icon';
import { accountsStore } from './store/accountsStore';
import { settingsStore } from './store/settingsStore';
import { invitesStore } from './store/invitesStore';
import { beaconQueue } from './beaconQueue';

const API = '';   // same-origin in prod; dev proxies /api → :3001 (client/package.json "proxy")
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SUPPORT_EMAIL = 'support@ibetteragent.com';

// Every per-account data store, keyed as `<base>__<accountId>`. Used to back up and
// to fully purge an organization's members' data on deletion.
const ORG_DATA_BASES = ['ira_contacts', 'ira_properties', 'ira_assignments', 'ira_custom_templates', 'ira_templates', 'ira_run_history'];

const NAV_ITEMS = [
  { id: 'contacts', labelKey: 'nav.contacts', icon: 'contacts' },
  { id: 'properties', labelKey: 'nav.properties', icon: 'properties' },
  { id: 'reminder-templates', labelKey: 'nav.templates', icon: 'reminder-templates' },
  { id: 'reminder-assignments', labelKey: 'nav.assigned', icon: 'reminder-assignments' },
  { id: 'run-reminders', labelKey: 'nav.run', icon: 'run-reminders' },
];

// Compact labels for the mobile bottom tab bar (all items always shown, no "More").
const TAB_SHORT = {
  contacts: 'Contacts', properties: 'Properties', 'reminder-templates': 'Templates',
  'reminder-assignments': 'Assign', 'run-reminders': 'History', files: 'Files',
  'supported-mls': 'MLS', admin: 'Agents', 'message-agents': 'Message',
};

// Manage-MLS drill-in sub-tabs — rendered inline on desktop and as a native
// bottom bar on mobile (full labels on desktop, short labels in the bottom bar).
const MLS_SUBTABS = [
  { id: 'orgs',      icon: 'organizations', label: 'Organizations',     short: 'Orgs' },
  { id: 'reminders', icon: 'reminders', label: 'Reminder Templates', short: 'Reminders' },
  { id: 'files',     icon: 'files',     label: 'Files',             short: 'Files' },
  { id: 'message',   icon: 'shield-check', label: 'Message Agents',    short: 'Message' },
  { id: 'process',   icon: 'process', label: 'Process',           short: 'Process' },
  { id: 'settings',  icon: 'settings', label: 'Settings',          short: 'Settings' },
];

// System-Org drill-in sub-tabs (one level below the MLS) — same desktop-inline /
// mobile-bottom-bar treatment as MLS_SUBTABS.
const ORG_SUBTABS = [
  { id: 'reminders', icon: 'reminders', label: 'Reminder Templates', short: 'Reminders' },
  { id: 'files',     icon: 'files',     label: 'Files',             short: 'Files' },
  { id: 'users',     icon: 'admin',     label: 'Manage Agents',     short: 'Agents' },
  { id: 'invite',    icon: 'mail',      label: 'Invite',            short: 'Invite' },
  { id: 'message',   icon: 'shield-check', label: 'Message Agents',    short: 'Message' },
];
// All orgs — including the iBetterAgent-owned System Org — get the full set of
// sub-tabs, so iBetterAgent can send org-scoped invites directly into a System Org.
const orgSubtabsFor = (org) => ORG_SUBTABS;

// Top-level System console tabs — same desktop-inline / mobile-bottom-bar treatment.
const SYS_TABS = [
  { id: 'mls',       icon: 'mls',       label: 'Supported MLS',    short: 'MLS' },
  { id: 'reminders', icon: 'reminders', label: 'System Reminders', short: 'Reminders' },
  { id: 'process',   icon: 'process',   label: 'Process',          short: 'Process' },
  { id: 'settings',  icon: 'settings',  label: 'Settings',         short: 'Settings' },
];

function loadAccounts() {
  // API mode: identity is server-owned. Don't seed default localStorage accounts
  // or run the localStorage tenancy bootstrap — accounts arrive via hydrateAll().
  if (usingApi()) return [];
  let arr = null;
  try { arr = JSON.parse(localStorage.getItem('ira_accounts') || 'null'); } catch {}
  if (!Array.isArray(arr) || !arr.length) {
    try { const a = JSON.parse(localStorage.getItem('ira_agent') || 'null'); arr = (a && a.username) ? [a] : []; } catch { arr = []; }
  }
  if (!arr.some(a => a.admin || a.system)) {
    const existing = arr.find(a => a.username && a.username.toLowerCase() === 'admin');
    if (existing) existing.admin = true;
    else arr.push({ username: 'admin', password: 'admin', admin: true, email: '', alias: 'Administrator', phone: '', licenseId: '', photo: '' });
  }
  localStorage.setItem('ira_accounts', JSON.stringify(arr));
  migrateLegacyData(arr);                  // legacy bare keys → __username
  const { accounts } = ensureTenancy(arr); // ids + default org + __username → __id + system admin
  ensureSystemBaseTemplates(systemScope(accounts));   // seed the private base templates
  ensureSystemDefaultMessages(systemScope(accounts)); // seed default built-in reminder wording
  return accounts;
}
// In API mode the MLS catalog is server-owned; we hold a synchronous in-memory
// mirror (`mlsCache`) refreshed from the API, so the many inline loadSystemMls()
// call sites keep working without each becoming async. localStorage path unchanged.
let mlsCache = [];
function loadSystemMls() {
  if (usingApi()) return mlsCache;
  try { return JSON.parse(localStorage.getItem('ira_mls__system') || '[]'); } catch { return []; }
}

// ── localStorage ↔ server mirror (API mode) ──────────────────────────────────
// Install a one-time write-through for server-mirrored scoped keys. First-class data
// (contacts/properties/reminders) now persists via an explicit AWAITED save in its
// components (syncState.putScoped) and is excluded from this mirror; what remains here
// are secondary keys (run history, archives, the MLS catalog, etc.) that still ride
// the localStorage write-through — but now through the SAME reliable, retrying,
// confirmed path (syncState), with one shared save indicator + unload beacon.
const __origSetItem = typeof window !== 'undefined' ? localStorage.setItem.bind(localStorage) : null;
if (usingApi() && typeof window !== 'undefined' && !window.__iraMirrorInstalled) {
  window.__iraMirrorInstalled = true;
  const origRemove = localStorage.removeItem.bind(localStorage);
  window.__iraPendingCount = syncState.pendingCount;

  localStorage.setItem = (key, value) => {
    __origSetItem(key, value);
    if (isMirroredKey(key)) syncState.mirror(key, value);
  };
  localStorage.removeItem = (key) => {
    origRemove(key);
    if (isMirroredKey(key)) syncState.mirrorRemove(key);
  };
  const flushAll = () => { beaconQueue.flush(); syncState.flushBeacon(); };
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flushAll(); });
  window.addEventListener('pagehide', flushAll);
}
// Repopulate localStorage from a { key: data } map without triggering write-through.
function hydrateDocs(docs) {
  Object.keys(docs || {}).forEach(key => {
    const v = docs[key];
    __origSetItem(key, typeof v === 'string' ? v : JSON.stringify(v));
  });
}
// Load orgs and make sure every supported MLS has its System Org (iBetterAgent direct).
function loadOrgsWithSystem() {
  if (usingApi()) return [];   // orgs are server-owned in API mode (hydrateAll loads them)
  const { orgs, changed } = ensureSystemOrgs(loadOrgs(), loadSystemMls());
  if (changed) saveOrgs(orgs);
  return orgs;
}
const DEFAULT_SIGNUP_TRIAL_DAYS = 30;
function loadSettings() {
  let s = {}; try { s = JSON.parse(localStorage.getItem('ira_settings') || '{}') || {}; } catch {}
  if (s.signupTrialDays == null) s.signupTrialDays = DEFAULT_SIGNUP_TRIAL_DAYS;
  if (s.publicSignups == null) s.publicSignups = false;   // invite-only until launch
  if (s.devMode == null) s.devMode = true;                // developer tools ON during dev
  return s;
}
function loadResets() { try { return JSON.parse(localStorage.getItem('ira_resets') || '{}'); } catch { return {}; } }
function saveResets(r) { localStorage.setItem('ira_resets', JSON.stringify(r)); }

function App() {
  const { t, lang, setLang } = useI18n();
  const [accounts, setAccounts]   = useState(loadAccounts);
  const [orgs, setOrgs]           = useState(loadOrgsWithSystem);
  const [settings, setSettings]   = useState(loadSettings);
  const bannerInputRef            = useRef(null);   // hidden file input for the header company banner
  const [mlsRev, setMlsRev]       = useState(0);   // bump to re-read the system MLS catalog / re-mount drill-in views after a hydrate
  const [currentId, setCurrentId] = useState(() => localStorage.getItem('ira_current') || null);
  // When API auth is on, we resolve the session from /api/auth/me on boot; until
  // that resolves we hold the UI so we don't flash the login screen.
  const [apiBooting, setApiBooting] = useState(usingApi());
  const [activeSection, setActiveSection] = useState('contacts');
  const [focusContactId, setFocusContactId] = useState(null);   // contact to auto-open in Edit (e.g. from a property's linked contact)
  const [resetToken, setResetToken] = useState(() => new URLSearchParams(window.location.search).get('reset'));
  const [resetInfo, setResetInfo] = useState({ loading: true, valid: false, username: '' });
  // Email-verification link for a public self-sign-up (?verify=<token>). Clicking it
  // creates the account and signs the new user in.
  const [verifyToken, setVerifyToken] = useState(() => new URLSearchParams(window.location.search).get('verify'));
  const [verifyState, setVerifyState] = useState({ loading: !!new URLSearchParams(window.location.search).get('verify'), error: '' });
  const [impersonator, setImpersonator] = useState(() => localStorage.getItem('ira_impersonator') || null);
  // Invite links (?signup=1) land prospects straight on the sign-up form.
  const signupIntent = new URLSearchParams(window.location.search).get('signup') === '1';
  // Single-use invite token (?signup=1&invite=<token>). Resolved from the server (public)
  // — it's authoritative for the org/MLS scope + email, and is consumed on register so a
  // link can never create a second account. inviteInfo: {orgId,mlsId,email,orgName,mlsName,
  // used,expired} or {invalid:true}.
  const inviteToken = new URLSearchParams(window.location.search).get('invite');
  const [inviteInfo, setInviteInfo] = useState(null);
  useEffect(() => {
    if (!inviteToken || !usingApi()) return;
    let alive = true;
    invitesStore.resolve(inviteToken)
      .then(info => { if (alive) setInviteInfo(info); })
      .catch(() => { if (alive) setInviteInfo({ invalid: true }); });
    return () => { alive = false; };
  }, [inviteToken]);
  const inviteUnusable = !!(inviteToken && inviteInfo && (inviteInfo.invalid || inviteInfo.used || inviteInfo.expired));
  // MLS lock: from the invite token (preferred) or a ?mls= param. Only honored if it
  // matches a real MLS in the catalog (token carries its name too).
  const lockedMls = (() => {
    if (inviteInfo && inviteInfo.mlsId) return { id: inviteInfo.mlsId, name: inviteInfo.mlsName || loadSystemMls().find(m => m.id === inviteInfo.mlsId)?.name || '' };
    const id = new URLSearchParams(window.location.search).get('mls');
    return id ? loadSystemMls().find(m => m.id === id) || null : null;
  })();
  // Email lock: from the invite token (preferred) or a ?email= param.
  const lockedEmail = (() => {
    const e = ((inviteInfo && inviteInfo.email) || new URLSearchParams(window.location.search).get('email') || '').trim();
    return EMAIL_RE.test(e) ? e : null;
  })();
  // Org lock: from the invite token (preferred), else a ?org= param resolved from the
  // server for the unauthenticated signup screen (falling back to loaded orgs).
  const orgParam = new URLSearchParams(window.location.search).get('org');
  const [inviteOrg, setInviteOrg] = useState(null);
  useEffect(() => {
    if (!orgParam || !usingApi()) return;
    let alive = true;
    orgStore.invite(orgParam)
      .then(o => { if (alive && o && o.id) setInviteOrg(o); })
      .catch(() => {});
    return () => { alive = false; };
  }, [orgParam]);
  const lockedOrg = (inviteInfo && inviteInfo.orgId)
    ? { id: inviteInfo.orgId, name: inviteInfo.orgName, mlsId: inviteInfo.mlsId, mlsName: inviteInfo.mlsName }
    : (inviteOrg || (orgParam ? orgs.find(x => x.id === orgParam) || null : null));
  const [showAuth, setShowAuth] = useState(signupIntent);
  const [systemTab, setSystemTab] = useState('mls');   // system console: 'mls' | 'reminders' | 'invite' | 'process' | 'settings'
  const [manageSysOrgId, setManageSysOrgId] = useState(null);  // System Org being managed (drill-in)
  const [manageSysTab, setManageSysTab] = useState('reminders'); // 'reminders' | 'users'
  const [manageMlsId, setManageMlsId] = useState(null);        // MLS being managed (drill-in)
  const [manageMlsTab, setManageMlsTab] = useState('orgs');    // 'orgs' | 'reminders' | 'files'

  // Resolve the signed-in account by id (fall back to username for the one-time
  // transition from the pre-tenancy `ira_current`).
  const agent = accounts.find(a => a.id === currentId) || accounts.find(a => a.username === currentId);

  // Apply the signed-in user's saved language so it follows them across devices.
  // `fromServer` skips the write-back (we're reflecting what the server already has).
  useEffect(() => {
    if (agent?.lang && agent.lang !== lang) setLang(agent.lang, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?.lang]);

  // API-auth mode: merge a server user into the in-memory accounts list so the
  // rest of the app (which derives `agent` from `accounts`) keeps working while
  // the other domains migrate. (No-op for the localStorage path.)
  function ingestApiUser(user) {
    setAccounts(prev => [...prev.filter(a => a.id !== user.id), user]);
    setCurrentId(user.id);
  }

  // Resolve a reset/setup token to its account (username + validity) so the reset page
  // can show whose password is being set. Server-validated in API mode; localStorage otherwise.
  useEffect(() => {
    if (!resetToken) { setResetInfo({ loading: false, valid: false, username: '' }); return; }
    if (!usingApi()) {
      const entry = loadResets()[resetToken];
      const valid = !!entry && entry.expires > Date.now();
      setResetInfo({ loading: false, valid, username: valid ? (accounts.find(a => a.id === entry.id)?.username || '') : '' });
      return;
    }
    let alive = true;
    setResetInfo({ loading: true, valid: false, username: '' });
    fetch(`${API}/api/auth/reset-info?token=${encodeURIComponent(resetToken)}`)
      .then(r => r.json())
      .then(d => { if (alive) setResetInfo({ loading: false, valid: !!d.valid, username: d.username || '' }); })
      .catch(() => { if (alive) setResetInfo({ loading: false, valid: false, username: '' }); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetToken]);

  // Email-verification link clicked: create the pending account NOW and sign in.
  // Guard so it fires exactly once (React StrictMode double-invokes effects in dev,
  // which would otherwise race two verify requests for the same token).
  // The ref guarantees this fires exactly once, so we must NOT cancel the in-flight
  // request on StrictMode's simulated unmount — otherwise the (only) request's result
  // gets discarded and the "Verifying…" screen hangs forever. Just apply the result.
  const verifyFiredRef = useRef(false);
  useEffect(() => {
    if (!verifyToken || verifyFiredRef.current) return;
    if (!usingApi()) { setVerifyState({ loading: false, error: 'Email verification requires the server.' }); return; }
    verifyFiredRef.current = true;
    setVerifyState({ loading: true, error: '' });
    (async () => {
      try {
        const user = await authStore.verifySignup(verifyToken);
        await hydrateAll(user);
        ingestApiUser(user);
        setActiveSection('getting-started');
        window.history.replaceState({}, '', window.location.pathname);   // drop ?verify=…
        setVerifyToken(null);
        setVerifyState({ loading: false, error: '' });
      } catch (err) {
        setVerifyState({ loading: false, error: err.message || 'Verification failed. Please sign up again.' });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyToken]);

  // On boot, if API auth is on: load the (public) MLS catalog, resolve the
  // session, and — if signed in — load the orgs the user may see.
  useEffect(() => {
    if (!usingApi()) return;
    let alive = true;
    (async () => {
      // Settings are public and needed by the signup screen (publicSignups gate + trial).
      try { const s = await settingsStore.get(); if (alive && s) setSettings(prev => ({ ...prev, ...s })); } catch {}
      let user = null, impersonatedBy = null;
      try { const sess = await authStore.me(); if (sess) { user = sess.user; impersonatedBy = sess.impersonatedBy; } } catch {}
      if (!alive) return;
      if (user) {
        await hydrateAll(user);
        if (!alive) return;
        ingestApiUser(user);
        if (impersonatedBy) setImpersonator(impersonatedBy);   // restore the support-access banner
      } else {
        try { mlsCache = await mlsStore.list(); } catch {}   // public catalog for the signup picker
        setCurrentId(null);
      }
      if (alive) { setMlsRev(r => r + 1); setApiBooting(false); }
    })();
    return () => { alive = false; };
  }, []);

  // Pull the signed-in user's world from the server into local state/localStorage:
  // MLS catalog, scoped data docs (→ localStorage), the user directory, and orgs.
  async function hydrateAll(user) {
    if (!usingApi()) return;
    try { mlsCache = await mlsStore.list(); } catch {}
    try { hydrateDocs(await docStore.loadMine()); } catch {}
    // Seed the System's base reminder templates under its API scope (write-through
    // persists them) so the cascade has a root. Idempotent.
    if (user && user.system) {
      try { ensureSystemBaseTemplates(user.id); ensureSystemDefaultMessages(user.id); } catch {}
    }
    try {
      const users = await accountsStore.list();
      setAccounts(user && !users.some(u => u.id === user.id) ? [...users, user] : users);
    } catch {}
    try { setOrgs(await orgStore.list()); } catch {}
    setMlsRev(r => r + 1);
  }

  // Refresh the server-owned MLS catalog / orgs into local state after a mutation.
  async function reloadMls() {
    if (usingApi()) { try { mlsCache = await mlsStore.list(); } catch {} }
    setMlsRev(r => r + 1);
  }
  async function reloadOrgs() {
    if (!usingApi()) return;
    try { setOrgs(await orgStore.list()); } catch {}
  }
  // Refresh the user directory (needed so masterScope() can resolve a newly-created
  // org admin — the org's reminders live under that admin's scope).
  async function reloadAccounts() {
    if (!usingApi()) return;
    // Also mirror to ira_accounts localStorage — the reminder engine (buildJobs) reads
    // it to resolve the owning agent's name + email signature.
    try { const list = await accountsStore.list(); setAccounts(list); localStorage.setItem('ira_accounts', JSON.stringify(list)); } catch {}
  }

  function persistAccounts(next) { setAccounts(next); localStorage.setItem('ira_accounts', JSON.stringify(next)); }

  // Mirror settings into localStorage so localStorage readers (notably isDevMode())
  // reflect the server-backed settings in API mode — otherwise toggling Developer
  // mode off in System Settings wouldn't hide the dev tools.
  useEffect(() => {
    try { localStorage.setItem('ira_settings', JSON.stringify(settings)); } catch {}
  }, [settings]);

  // Entering an org drill-in: a regular org's reminders live under its ADMIN's scope,
  // which may not be in `accounts` yet right after the org is created (so masterScope
  // would briefly resolve to the wrong scope and show nothing). Re-sync accounts +
  // scoped docs on entry — the same data a full refresh would load — so inherited
  // reminders show on the first try, no manual refresh needed.
  useEffect(() => {
    if (!usingApi() || !manageSysOrgId) return;
    (async () => {
      try { await reloadAccounts(); } catch {}
      try { hydrateDocs(await docStore.loadMine()); setMlsRev(r => r + 1); } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manageSysOrgId]);

  // Entering an MLS drill-in: same idea — re-sync the catalog, orgs, accounts, and
  // scoped docs so the MLS's (and its System Org's) inherited reminders appear right
  // away, instead of only after a manual page refresh.
  useEffect(() => {
    if (!usingApi() || !manageMlsId) return;
    (async () => {
      try { await reloadMls(); } catch {}
      try { await reloadOrgs(); } catch {}
      try { await reloadAccounts(); } catch {}
      try { hydrateDocs(await docStore.loadMine()); setMlsRev(r => r + 1); } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manageMlsId]);
  function persistOrgs(next) { setOrgs(next); saveOrgs(next); }
  function updateSettings(patch) {
    const next = { ...settings, ...patch };
    setSettings(next);
    if (usingApi()) { settingsStore.update(patch).catch(() => {}); return; }
    localStorage.setItem('ira_settings', JSON.stringify(next));
  }
  // Patch one MLS catalog entry (ira_mls__system). A patch value of `undefined`
  // deletes that key (e.g. clearing an override to inherit System).
  function updateSystemMls(id, patch) {
    if (usingApi()) {
      // Server tri-state: send null where the localStorage path used `undefined`
      // (clearing an override to inherit System).
      const body = {};
      Object.keys(patch).forEach(k => { body[k] = patch[k] === undefined ? null : patch[k]; });
      mlsStore.update(id, body).then(reloadMls).catch(() => {});
      return;
    }
    const list = loadSystemMls().map(m => {
      if (m.id !== id) return m;
      const next = { ...m, ...patch };
      Object.keys(patch).forEach(k => { if (patch[k] === undefined) delete next[k]; });
      return next;
    });
    localStorage.setItem('ira_mls__system', JSON.stringify(list));
    setMlsRev(r => r + 1);
  }
  // MLS create from the SupportedMls "+ Add MLS" form (API mode only).
  async function createMls({ name, description }) {
    const { mls } = await mlsStore.create({ name, description });
    await reloadMls();              // new MLS appears in the catalog/list; close the panel now
    reloadOrgs().catch(() => {});   // refresh orgs (incl. the auto-created System Org) in the background
    // No eager doc hydrate here: opening the MLS drill-in re-syncs its scoped docs and
    // remounts the reminder view (keyed on the hydration revision), so inherited
    // reminders show on entry without blocking create on a heavy reconcile.
    return mls;
  }
  async function updateMlsApi(id, patch) {
    await mlsStore.update(id, patch);
    await reloadMls();
  }

  // If a signed-in member's organization is blocked (suspended, or trial expired
  // with no purchased seats), log them out entirely. (A full seat count does NOT do
  // this — the org stays active; they just can't add more users.)
  useEffect(() => {
    if (agent && agent.orgId) {
      const o = orgById(orgs, agent.orgId);
      if (orgBlockedReason(o)) { logout(); return; }
    }
    // Direct (System Org) members are gated individually — log out when their term ends.
    if (agent && !agent.system && userExpired(agent)) logout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent, orgs]);

  // Keep a System Org for every supported MLS in sync while the operator is in the
  // console (e.g. right after they add a new MLS in the Supported MLS tab).
  useEffect(() => {
    if (usingApi()) return;   // API mode: System Orgs are created server-side (on signup).
    if (!agent || !agent.system) return;
    const { orgs: ensured, changed } = ensureSystemOrgs(orgs, loadSystemMls());
    if (changed) persistOrgs(ensured);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [systemTab, agent]);

  // Reminder cascade is System → MLS → Org → Agent. Seed the masters down the chain
  // (seedTemplates only fills missing entries, so this is safe to re-run and never
  // clobbers later edits). Each MLS master inherits the system's public reminders;
  // each System Org (direct signups) inherits from its MLS.
  useEffect(() => {
    if (usingApi()) return;   // API mode: the server owns the cascade + orphan cleanup;
                              // this localStorage self-heal would wrongly purge server data.
    const sys = systemScope(accounts);
    if (!sys) return;
    purgeOrphanScopes();   // drop any data whose owning account/org/MLS no longer exists
    loadSystemMls().forEach(m => { seedTemplates(sys, m.id); });
    orgs.filter(o => o.systemOrg).forEach(o => {
      seedTemplates(sys, o.id);
      seedTemplates(o.mlsId || sys, o.id);
    });
    // Strict-recursion cleanup: every level inherits ONLY the reminders the level above
    // explicitly made Public. Enforce that here so stale/over-seeded copies self-heal at
    // each hop — a built-in is kept only if it's modified or the level above holds it Public.
    const sysCustom = JSON.parse(localStorage.getItem(scopedKey('ira_custom_templates', sys)) || '[]');
    const pruneBuiltins = (scope, parentCustom) => {
      const key = scopedKey('ira_custom_templates', scope);
      let list = JSON.parse(localStorage.getItem(key) || '[]');
      const before = list.length;
      list = list.filter(c => {
        if (!c.builtin) return true;
        if (c.modified) return true;
        return parentCustom.some(x => x.builtin && x.id === c.id && x.public === true);
      });
      if (list.length !== before) localStorage.setItem(key, JSON.stringify(list));
      return list;
    };
    // MLS: drop unmodified built-ins the System hasn't published.
    loadSystemMls().forEach(m => pruneBuiltins(m.id, sysCustom));
    orgs.filter(o => !o.systemOrg).forEach(o => {
      const orgAdminScope = masterScope(accounts, o.id);
      if (!orgAdminScope) return;
      // Org admin: drop unmodified built-ins the MLS hasn't published down to it.
      const mlsCustom = o.mlsId ? JSON.parse(localStorage.getItem(scopedKey('ira_custom_templates', o.mlsId)) || '[]') : [];
      const orgAdminCustom = pruneBuiltins(orgAdminScope, mlsCustom);
      // Agents: drop unmodified built-ins the org admin hasn't published down to them.
      orgAgents(accounts, o.id).forEach(a => pruneBuiltins(a.id, orgAdminCustom));
    });
    // System Orgs (direct signups): members follow the SAME rule — drop unmodified
    // built-ins the System Org itself hasn't published. The org's reminders live at o.id.
    orgs.filter(o => o.systemOrg).forEach(o => {
      const orgCustom = JSON.parse(localStorage.getItem(scopedKey('ira_custom_templates', o.id)) || '[]');
      orgAgents(accounts, o.id).forEach(a => pruneBuiltins(a.id, orgCustom));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgs.length, systemTab]);
  const resolveInOrg = (username) => accounts.find(a => a.orgId === agent?.orgId && a.username === username);

  // ── Auth ──────────────────────────────────────────────────────────────────
  // Usernames are globally unique, so login is just username + password — no need
  // to pick an organization, and system/admin accounts aren't advertised anywhere.
  async function login(username, password) {
    if (usingApi()) {
      try {
        const user = await authStore.login((username || '').trim(), password);
        await hydrateAll(user);
        ingestApiUser(user);
        setActiveSection(user.system ? 'system' : (user.admin ? 'reminder-templates' : 'contacts'));
        return null;
      } catch (err) {
        return err.message || 'Sign-in failed.';
      }
    }
    const u = (username || '').trim();
    const acct = accounts.find(a => a.username === u && a.password === password);
    if (!acct) return 'Incorrect username or password.';
    if (acct.disabled) return 'This account has been disabled. Contact your administrator.';
    if (acct.orgId) {
      const org = orgById(orgs, acct.orgId);
      const blocked = orgBlockedReason(org);
      if (blocked === 'suspended') return `This organization is suspended. Contact iBetterAgent at ${SUPPORT_EMAIL}.`;
      if (blocked === 'expired') return `Your organization’s free trial has ended. Contact iBetterAgent at ${SUPPORT_EMAIL} to purchase seats and restore access.`;
    }
    if (!acct.system && userExpired(acct)) return `Your iBetterAgent access has expired. Contact iBetterAgent at ${SUPPORT_EMAIL} to renew your subscription.`;
    localStorage.setItem('ira_current', acct.id);
    setActiveSection(acct.system ? 'system' : (acct.admin ? 'reminder-templates' : 'contacts'));
    setCurrentId(acct.id);
    return null;
  }
  // Self-signup joins the System Org for the MLS the agent picks (iBetterAgent direct).
  // The org never expires, but each direct member gets an individual trial term so the
  // contract is per-person. Org members (non-direct) are provisioned by their org admin.
  async function register({ userName, name, password, mlsId, email, orgId }) {
    if (usingApi()) {
      try {
        const resp = await authStore.register({
          userName, password, mlsId, email, orgId, alias: name,
          // The single-use token (if the link carried one) — server consumes it.
          inviteToken: inviteToken || undefined,
          // Org invites + invite links (?signup=1, incl. MLS-scoped) are "invites";
          // a plain MLS pick is not — so the server's publicSignups gate stays meaningful.
          invite: signupIntent || !!orgId || !!inviteToken,
          // Build the verification link against the client origin, not the API host.
          appOrigin: window.location.origin,
        });
        // Anonymous public sign-up: no account yet — a verification email was sent.
        if (resp && resp.pending) {
          if (resp.verifyLink) console.log('[dev] sign-up verification link:', resp.verifyLink);
          return { pending: true, email: resp.email || email };
        }
        const user = resp.user;
        await hydrateAll(user);
        ingestApiUser(user);
        setActiveSection('getting-started');
        return null;
      } catch (err) {
        return err.message || 'Sign-up failed.';
      }
    }
    // Invite-only gate: allow only when public sign-ups are on, the visitor arrived
    // via an invitation link (?signup=1), or Developer mode is on. Safety net behind the UI.
    if (!settings.publicSignups && !signupIntent && !settings.devMode) {
      return 'Sign-ups are currently invite-only. Please use your invitation link.';
    }
    const u = (userName || '').trim();
    if (!u) return 'User name is required.';
    if (accounts.some(a => a.username.toLowerCase() === u.toLowerCase())) return 'That username is already taken.';
    const pwErr = passwordError(password); if (pwErr) return pwErr;
    // Email is optional, but if provided (e.g. pre-filled from an invite link) it must be valid.
    const em = (email || '').trim();
    if (em && !EMAIL_RE.test(em)) return 'Please enter a valid email address.';
    // Org-scoped invite: join the specific organization directly (it already belongs to
    // an MLS). Governed by the org's seats/trial — no personal expiry, like adminAddUser.
    if (orgId) {
      const org = orgById(orgs, orgId);
      if (!org) return 'That organization isn’t available.';
      const blocked = orgBlockedReason(org);
      if (blocked === 'suspended') return 'That organization is suspended. Contact your administrator.';
      if (blocked === 'expired') return 'That organization’s subscription has ended. Contact your administrator.';
      const acct = { id: genId('acc'), username: u, orgId: org.id, email: em, password, createdAt: new Date().toISOString(), alias: (name || '').trim(), phone: '', licenseId: '', photo: '' };
      const next = [...accounts, acct];
      if (seatsUsed(next, org.id) > orgEffectiveSeats(org)) return 'That organization has no seats available right now. Contact your administrator.';
      seedTemplates(masterScope(next, org.id), acct.id);   // inherit only the org's PUBLISHED reminders
      persistAccounts(next);
      localStorage.setItem('ira_current', acct.id);
      setActiveSection('getting-started');
      setCurrentId(acct.id);
      return null;
    }
    if (!mlsId) return 'Please select your MLS.';
    // Make sure the System Org for this MLS exists, then join it.
    const { orgs: ensured, changed } = ensureSystemOrgs(orgs, loadSystemMls());
    if (changed) persistOrgs(ensured);
    const sysOrg = systemOrgForMls(ensured, mlsId);
    if (!sysOrg) return "That MLS isn’t available yet. Please pick another.";
    const mlsEntry = loadSystemMls().find(m => m.id === mlsId);
    const days = Math.max(0, effectiveTrialDays(mlsEntry, settings));
    const expiresAt = days > 0 ? new Date(Date.now() + days * 86400000).toISOString() : null;
    const acct = { id: genId('acc'), username: u, orgId: sysOrg.id, email: em, password, expiresAt, createdAt: new Date().toISOString(), alias: (name || '').trim(), phone: '', licenseId: '', photo: '' };
    // Cascade System → MLS → System Org → member.
    const sysScope = systemScope(accounts);
    seedTemplates(sysScope, mlsId);
    seedTemplates(sysScope, sysOrg.id);   // System → SysOrg (full set, Private)
    seedTemplates(mlsId, sysOrg.id);      // MLS public customs on top
    seedTemplates(sysOrg.id, acct.id);   // member inherits only the System Org's PUBLISHED reminders
    persistAccounts([...accounts, acct]);
    localStorage.setItem('ira_current', acct.id);
    setActiveSection('getting-started');
    setCurrentId(acct.id);
    return null;
  }
  function logout() {
    if (usingApi()) authStore.logout().catch(() => {});
    localStorage.removeItem('ira_current');
    localStorage.removeItem('ira_impersonator');
    setImpersonator(null);
    setCurrentId(null);
    setShowAuth(false);
    // Drop any invite params (?signup=1&mls=…) so logging back in opens the LOGIN
    // form, not the invite Sign-Up form. (signupIntent/lockedMls are read from the URL.)
    if (window.location.search) window.history.replaceState(null, '', window.location.pathname);
  }

  // ── Admin impersonation (support access) ─────────────────────────────────
  async function impersonate(accountId, note) {
    const target = accounts.find(a => a.id === accountId);
    if (!target) return 'User not found.';
    const trimmed = (note || '').trim();
    if (!trimmed) return 'A support note is required to access this account.';
    if (usingApi()) {
      const adminName = agent?.username || null;
      try {
        const t = await authStore.impersonate(accountId, trimmed);   // server switches the session
        await hydrateAll(t);                                          // load the target's world
        ingestApiUser(t);
        setImpersonator(adminName);
        setActiveSection('contacts');
        return null;
      } catch (err) { return err.message || 'Could not access that account.'; }
    }
    const at = new Date().toISOString();
    persistAccounts(accounts.map(a => a.id === accountId
      ? { ...a, impersonations: [...(a.impersonations || []), { by: agent.username, at, note: trimmed }] }
      : a));
    localStorage.setItem('ira_impersonator', currentId);
    localStorage.setItem('ira_current', accountId);
    setImpersonator(currentId);
    setCurrentId(accountId);
    setActiveSection('contacts');
    return null;
  }
  async function exitImpersonation() {
    if (usingApi()) {
      try {
        const admin = await authStore.exitImpersonation();   // server restores the admin session
        await hydrateAll(admin);
        ingestApiUser(admin);
        setImpersonator(null);
        setActiveSection(admin.system ? 'system' : 'admin');
      } catch { /* fall through */ }
      return;
    }
    const back = impersonator;
    if (!back) return;
    localStorage.removeItem('ira_impersonator');
    localStorage.setItem('ira_current', back);
    setImpersonator(null);
    setCurrentId(back);
    setActiveSection('admin');
  }
  async function saveAgent(next) {
    if (usingApi()) {
      // Persist profile to the central User record so it follows the user across
      // devices (previously this only updated localStorage and was lost on a new browser).
      let updated;
      try {
        updated = await authStore.updateProfile({
          alias: next.alias, phone: next.phone, licenseId: next.licenseId,
          photo: next.photo, email: next.email, username: next.username,
          ...(next.mlsBrokerId !== undefined ? { mlsBrokerId: next.mlsBrokerId } : {}),
          ...(next.languages !== undefined ? { languages: next.languages } : {}),
          ...(next.signature !== undefined ? { signature: next.signature } : {}),
          ...(next.website !== undefined ? { website: next.website } : {}),
          ...(next.bio !== undefined ? { bio: next.bio } : {}),
          ...(next.socials !== undefined ? { socials: next.socials } : {}),
          ...(next.publicProfile !== undefined ? { publicProfile: next.publicProfile } : {}),
          ...(next.publicSlug !== undefined ? { publicSlug: next.publicSlug } : {}),
          ...(next.sectionOrder !== undefined ? { sectionOrder: next.sectionOrder } : {}),
          ...(next.hiddenSections !== undefined ? { hiddenSections: next.hiddenSections } : {}),
          ...(next.sectionTitles !== undefined ? { sectionTitles: next.sectionTitles } : {}),
          ...(next.password ? { password: next.password } : {}),
        });
        // Refresh the accounts list FIRST (for the engine / ira_accounts), then re-ingest
        // the full user LAST so the current agent keeps fields the leaner accounts-list
        // shape drops (publicProfile/publicSlug/signature/bio/socials) — otherwise the
        // reload would strip them off the just-saved agent.
        await reloadAccounts();   // refresh ira_accounts so the engine sees the updated signature
        ingestApiUser(updated);
      } catch (e) {
        console.error('Profile save failed:', e?.message || e);
      }
      return updated;
    }
    persistAccounts(accounts.map(a => a.id === next.id ? next : a));
  }

  // Toggle whether a file appears in this agent's public profile ("Market Reports").
  // Persisted to the agent's meta server-side; the accounts state mirrors it so the
  // Files screen reflects the choice immediately.
  async function toggleProfileFile(fileId, on) {
    const cur = Array.isArray(agent.profileFiles) ? agent.profileFiles : [];
    const nextList = on ? [...new Set([...cur, fileId])] : cur.filter(id => id !== fileId);
    persistAccounts(accounts.map(a => a.id === agent.id ? { ...a, profileFiles: nextList } : a));
    if (usingApi()) {
      try { await api.post('/api/files/profile', { fileId, on }); }
      catch (e) { console.error('Profile-file toggle failed:', e?.message || e); }
    }
  }

  // ── Reminder processing suspend (maintenance opt-out; default ON) ──────────
  // An agent can pause their own reminders; an org admin can pause the whole org.
  function setMyRemindersSuspended(suspended) {
    // Persist to the central User record so the SERVER-SIDE process honors the
    // agent's self-pause (it can't see browser-only state). Optimistically reflect
    // it locally too. Falls back to local-only in non-API (localStorage) mode.
    persistAccounts(accounts.map(a => a.id === agent.id ? { ...a, remindersSuspended: !!suspended } : a));
    if (usingApi()) {
      authStore.updateProfile({ remindersSuspended: !!suspended })
        .then(updated => ingestApiUser(updated))
        .catch(() => {});
    }
  }
  function setOrgRemindersSuspended(suspended) {
    if (!agent.orgId) return;
    if (usingApi()) { orgStore.update(agent.orgId, { remindersSuspended: !!suspended }).then(reloadOrgs).catch(() => {}); return; }
    persistOrgs(orgs.map(o => o.id === agent.orgId ? { ...o, remindersSuspended: !!suspended } : o));
  }

  // Manually push a newly created reminder down ONE level (ADD-only — never replaces an
  // existing reminder downstream). Cascade is System → MLS → Org → Agent:
  //   System Reminders → every MLS master
  //   org admin        → every agent in their org
  // (MLS → its orgs is pushMlsReminder.) Returns {pushed, total}.
  // API mode: the server makes the copies (with Name_1/Name_2 renaming) and we
  // re-hydrate so the new copies show up. localStorage mode keeps the inline loop.
  async function apiPushReminder(from, reminderId, targets) {
    if (!from || !targets.length) return { pushed: 0, total: targets.length };
    let r = { pushed: 0, total: targets.length };
    try { r = await docStore.push(from, reminderId, targets); } catch {}
    try { hydrateDocs(await docStore.loadMine()); } catch {}
    return { pushed: r.pushed || 0, total: r.total ?? targets.length };
  }
  function pushReminderDown(reminderId) {
    let from, targets;
    if (agent.system) {
      from = systemScope(accounts);
      targets = loadSystemMls().map(m => m.id);   // push to each MLS master
    } else if (agent.admin) {
      from = masterScope(accounts, agent.orgId);
      targets = orgAgents(accounts, agent.orgId).map(a => a.id);
    } else {
      return { pushed: 0, total: 0 };
    }
    if (usingApi()) return apiPushReminder(from, reminderId, targets);
    let pushed = 0;
    targets.forEach(t => { if (pushReminder(from, t, reminderId)) pushed++; });
    return { pushed, total: targets.length };
  }

  // MLS-level push: an MLS's reminder goes to its orgs (regular org masters) and to
  // its System Org's direct members. Add-only.
  function pushMlsReminder(mlsId, reminderId) {
    const sysOrgIds = new Set(orgs.filter(o => o.systemOrg && o.mlsId === mlsId).map(o => o.id));
    const targets = [
      ...orgs.filter(o => !o.systemOrg && o.mlsId === mlsId).map(o => masterScope(accounts, o.id)).filter(Boolean),
      ...accounts.filter(a => sysOrgIds.has(a.orgId)).map(a => a.id),
    ];
    if (usingApi()) return apiPushReminder(mlsId, reminderId, targets);
    let pushed = 0;
    targets.forEach(t => { if (pushReminder(mlsId, t, reminderId)) pushed++; });
    return { pushed, total: targets.length };
  }

  // Propagate a public reminder from an MLS to all its orgs (including system orgs).
  // Respects the `modified` flag: only updates orgs that haven't explicitly saved their copy.
  function propagateMlsReminder(mlsId, reminderId) {
    if (!mlsId || !reminderId) return;
    orgs.filter(o => o.mlsId === mlsId).forEach(o => {
      propagateReminder(mlsId, o.id, reminderId);   // org reminders live under org.id
    });
  }

  // Propagate a public reminder from an org to all its agents — the SAME rule as MLS→Org.
  // The org's "master" reminder scope is the org id for a System Org (direct signups) or
  // its first admin's scope otherwise. Respects the `modified` flag: only updates agents
  // that haven't explicitly saved their own copy.
  function propagateOrgReminder(orgId, reminderId) {
    if (!orgId || !reminderId) return;
    orgAgents(accounts, orgId).forEach(a => propagateReminder(orgId, a.id, reminderId));   // org reminders live under org.id
  }

  // Propagate a public reminder from the System level to all MLS scopes.
  // Respects the `modified` flag: only updates MLSes that haven't explicitly saved their copy.
  function propagateSystemReminder(fromScope, reminderId) {
    if (!fromScope || !reminderId) return;
    loadSystemMls().forEach(m => propagateReminder(fromScope, m.id, reminderId));
  }

  // ── Password reset via emailed link ──────────────────────────────────────
  async function requestReset(username, email) {
    if (usingApi()) {
      // API mode: the token must live in the DB (that's where /api/auth/reset validates
      // it). The server creates the reset row and emails the link; anti-enumeration means
      // it always returns ok. In dev (no mail configured) it returns the token to surface.
      const id = ((username || '').trim() || (email || '').trim());
      if (!id) return { error: 'Enter your login email.' };
      try {
        const data = await api.post('/api/auth/forgot', { username: id, email: id, appOrigin: window.location.origin });
        if (data && data.devToken) {
          return { ok: true, email: id, link: `${window.location.origin}/?reset=${data.devToken}`, warn: 'Mail not configured (dev) — use this link to reset:' };
        }
        return { ok: true, email: id };
      } catch (e) { return { error: e.message || 'Could not send the reset link.' }; }
    }
    const u = (username || '').trim();
    const acct = accounts.find(a => a.username.toLowerCase() === u.toLowerCase());
    if (!acct) return { error: 'No account found with that username.' };
    let targetEmail = (acct.email || '').trim();
    if (!targetEmail || !EMAIL_RE.test(targetEmail)) {
      const provided = (email || '').trim();
      if (!provided) return { needEmail: true };
      if (!EMAIL_RE.test(provided)) return { needEmail: true, error: 'Please enter a valid email address.' };
      targetEmail = provided;
      persistAccounts(accounts.map(a => a.id === acct.id ? { ...a, email: provided } : a));
    }
    const token = Math.random().toString(36).slice(2) + Date.now().toString(36);
    const resets = loadResets();
    resets[token] = { id: acct.id, expires: Date.now() + 3600000 };
    saveResets(resets);
    const link = `${window.location.origin}/?reset=${token}`;
    try {
      const res = await fetch(`${API}/api/send-reminder`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: targetEmail,
          subject: 'Reset your iBetterAgent password',
          body: `We received a request to reset your password.\n\nOpen this link to set a new password:\n${link}\n\nThe link expires in 1 hour. If you didn't request this, you can ignore this email.`,
        }),
      });
      if (!res.ok) throw new Error('send failed');
      return { ok: true, email: targetEmail };
    } catch {
      return { ok: true, email: targetEmail, link, warn: 'Mail server unavailable — use this link to reset:' };
    }
  }
  async function setPasswordByToken(token, newPassword) {
    const pwErr = passwordError(newPassword); if (pwErr) return pwErr;
    if (usingApi()) {
      // Server-validated reset (password reset OR org-admin setup link). On success the
      // user signs in with the new password — the ResetPassword "done" screen prompts it.
      try {
        const res = await fetch(`${API}/api/auth/reset`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, newPassword }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return data.error || 'This reset link is invalid or has expired.';
        // The server logs us in on reset — go straight into the app (no second sign-in).
        window.history.replaceState({}, '', window.location.pathname);
        setResetToken(null);
        if (data.user) {
          await hydrateAll(data.user);
          ingestApiUser(data.user);
          setActiveSection(data.user.system ? 'system' : (data.user.admin ? 'reminder-templates' : 'contacts'));
        }
        return null;
      } catch {
        return 'Could not reach the server. Please try again.';
      }
    }
    const resets = loadResets();
    const entry = resets[token];
    if (!entry || entry.expires < Date.now()) return 'This reset link is invalid or has expired.';
    const acct = accounts.find(a => a.id === entry.id);
    if (!acct) return 'This account no longer exists.';
    persistAccounts(accounts.map(a => a.id === entry.id ? { ...a, password: newPassword } : a));
    delete resets[token];
    saveResets(resets);
    window.history.replaceState({}, '', window.location.pathname);
    setResetToken(null);
    if (!acct.disabled) {
      localStorage.setItem('ira_current', acct.id);
      setActiveSection(acct.system ? 'system' : (acct.admin ? 'reminder-templates' : 'contacts'));
      setCurrentId(acct.id);
    }
    return null;
  }
  function finishReset() {
    window.history.replaceState({}, '', window.location.pathname);
    setResetToken(null);
  }

  // ── Org admin: manage agents within their org & seat limit ────────────────
  async function adminAddUser({ alias, email, admin, phone, licenseId, mlsBrokerId, inviteSubject, inviteMessage }) {
    const org = orgById(orgs, agent.orgId);
    const blocked = orgBlockedReason(org);
    if (blocked === 'suspended') return `This organization is suspended — you can’t add users. Contact iBetterAgent at ${SUPPORT_EMAIL}.`;
    if (blocked === 'expired') return `Your free trial has ended — purchase seats to add users. Contact iBetterAgent at ${SUPPORT_EMAIL}.`;
    const em = (email || '').trim();
    if (!em) return 'Email is required — it is the login, and the setup link is sent there.';
    if (!EMAIL_RE.test(em)) return 'Please enter a valid email address.';
    const u = em;   // the email IS the login/username (one account per email)
    if (accounts.some(a => a.username.toLowerCase() === u.toLowerCase())) return 'An account with this email already exists.';
    if (org && seatsUsed(accounts, agent.orgId) >= orgEffectiveSeats(org)) return 'Seat limit reached — increase seats or remove a member first.';
    if (usingApi()) {
      try {
        // Password-less: the server emails the new agent a one-time setup link.
        const resp = await accountsStore.create({ username: u, alias, email: em, orgId: agent.orgId, admin: !!admin, phone, licenseId, mlsBrokerId, appOrigin: window.location.origin, inviteSubject, inviteMessage });
        if (resp?.setupLink) console.log('[dev] agent setup link:', resp.setupLink);
        await reloadAccounts();
        hydrateDocs(await docStore.loadMine());
        return null;
      } catch (err) { return err.message || 'Could not add user.'; }
    }
    // localStorage fallback (dev-local): random temp password, forced change on first login.
    const acct = { id: genId('acc'), username: u, orgId: agent.orgId, email: em, password: (Math.random().toString(36).slice(2) + 'A1!'), admin: !!admin, mustChangePassword: true, createdAt: new Date().toISOString(), alias: (alias || '').trim(), phone: (phone || '').trim(), licenseId: (licenseId || '').trim(), mlsBrokerId: (mlsBrokerId || '').trim(), photo: '', history: [] };
    const next = [...accounts, acct];
    // Seat limit against the org's CURRENT capacity — trial seats during the trial,
    // purchased seats after.
    if (org && seatsUsed(next, agent.orgId) > orgEffectiveSeats(org)) return 'Seat limit reached — increase seats or remove a member first.';
    if (!admin) seedTemplates(masterScope(next, agent.orgId), acct.id);   // org agents inherit only the org's PUBLISHED reminders
    persistAccounts(next);
    return null;
  }
  function adminDeleteUsers(usernames) {
    const set = new Set(usernames || []);
    const idset = new Set(accounts.filter(a => a.orgId === agent.orgId && set.has(a.username) && !a.admin).map(a => a.id));
    if (!idset.size) return;
    purgeDataForScopes({ acctIds: [...idset] });   // wipe each removed agent's contacts/properties/templates/files
    persistAccounts(accounts.filter(a => !idset.has(a.id)));
    if (usingApi()) Promise.all([...idset].map(id => accountsStore.remove(id).catch(() => {}))).then(reloadAccounts);
  }
  // Purge EVERY localStorage trace for a set of account / org / MLS ids: all
  // `ira_*__<id>` scope keys (contacts, properties, assignments, templates, custom
  // templates, auto-enroll, attachments, run history, …) AND shared file-library
  // entries owned by them plus their archived-file lists. Used whenever an account,
  // org, or MLS is removed so nothing is orphaned in storage.
  function purgeDataForScopes({ acctIds = [], orgIds = [], mlsIds = [] }) {
    const ids = [...acctIds, ...orgIds, ...mlsIds];
    if (ids.length) {
      for (const k of Object.keys(localStorage)) {
        if (ids.some(id => k.endsWith('__' + id))) localStorage.removeItem(k);
      }
    }
    // File library: one global store keyed by owner `agent:<id>` / `org:<id>` / `mls:<id>`.
    const ownerKeys = [...acctIds.map(id => 'agent:' + id), ...orgIds.map(id => 'org:' + id), ...mlsIds.map(id => 'mls:' + id)];
    if (ownerKeys.length) {
      const ownerSet = new Set(ownerKeys);
      try {
        const files = JSON.parse(localStorage.getItem('ira_files') || '{}');
        let changed = false;
        for (const fid of Object.keys(files)) {
          if (files[fid] && ownerSet.has(files[fid].owner)) { delete files[fid]; changed = true; }
        }
        if (changed) localStorage.setItem('ira_files', JSON.stringify(files));
      } catch {}
      ownerKeys.forEach(ok => localStorage.removeItem('ira_files_hidden__' + ok));
    }
  }
  // Sweep ALL orphaned data below System: any `ira_<base>__<scope>` key (or shared-file
  // record / archive list) whose owning account, org, or MLS no longer exists. Such data
  // is unreachable — nothing references its owner — so removing it is always safe. Runs on
  // load as a safety net so any deletion path that ever misses a scope can't leave litter.
  function purgeOrphanScopes() {
    const valid = new Set(['system']);   // 'system' = the global MLS catalog scope
    try { JSON.parse(localStorage.getItem('ira_accounts') || '[]').forEach(a => a && valid.add(a.id)); } catch {}
    try { JSON.parse(localStorage.getItem('ira_orgs') || '[]').forEach(o => o && valid.add(o.id)); } catch {}
    try { JSON.parse(localStorage.getItem('ira_mls__system') || '[]').forEach(m => m && valid.add(m.id)); } catch {}
    // Bases whose suffix after `__` is a tenant scope id (account / org / MLS / 'system').
    const SCOPED = ['ira_contacts', 'ira_properties', 'ira_assignments', 'ira_custom_templates',
      'ira_templates', 'ira_autoenroll', 'ira_attachments', 'ira_run_history', 'ira_mls'];
    const ownerOk = (owner) => { const i = (owner || '').indexOf(':'); return i !== -1 && valid.has(owner.slice(i + 1)); };
    const removed = [];
    for (const k of Object.keys(localStorage)) {
      const idx = k.lastIndexOf('__');
      if (idx === -1) continue;
      const base = k.slice(0, idx), scope = k.slice(idx + 2);
      if (SCOPED.includes(base)) {
        if (!valid.has(scope)) { localStorage.removeItem(k); removed.push(k); }
      } else if (base === 'ira_files_hidden') {
        if (!ownerOk(scope)) { localStorage.removeItem(k); removed.push(k); }
      }
    }
    // Shared file-library records whose owner (agent:/org:/mls:<id>) no longer exists.
    try {
      const files = JSON.parse(localStorage.getItem('ira_files') || '{}');
      let changed = false;
      for (const fid of Object.keys(files)) {
        if (files[fid] && !ownerOk(files[fid].owner)) { delete files[fid]; changed = true; }
      }
      if (changed) localStorage.setItem('ira_files', JSON.stringify(files));
    } catch {}
    return removed;
  }
  function adminSetDisabled(usernames, disabled) {
    const at = new Date().toISOString();
    const set = new Set(usernames || []);
    // Re-enabling consumes seats (agents, and admins beyond the first) — block it
    // if it would push the org over capacity. Project the enable, then count.
    if (!disabled) {
      const org = orgById(orgs, agent.orgId);
      const projected = accounts.map(a =>
        (a.orgId === agent.orgId && set.has(a.username) && a.id !== currentId && a.disabled) ? { ...a, disabled: false } : a);
      if (org && seatsUsed(projected, agent.orgId) > orgEffectiveSeats(org)) {
        return 'Seat limit reached — cannot enable. Increase seats or remove/keep-disabled a member first.';
      }
    }
    const ids = accounts.filter(a => a.orgId === agent.orgId && set.has(a.username) && a.id !== currentId && !!a.disabled !== disabled).map(a => a.id);
    persistAccounts(accounts.map(a => {
      if (a.orgId !== agent.orgId || !set.has(a.username) || a.id === currentId) return a;
      if (!!a.disabled === disabled) return a;
      const history = [...(a.history || []), { action: disabled ? 'disabled' : 'enabled', at, by: agent.username }];
      return { ...a, disabled, history };
    }));
    if (usingApi() && ids.length) Promise.all(ids.map(id => accountsStore.update(id, { disabled }).catch(() => {}))).then(reloadAccounts);
    return null;
  }

  // Edit an agent's profile details (name/email/phone/license/broker) — org admin over
  // their own org, or system. Central User record is the single source of truth.
  async function updateMember(id, patch) {
    if (usingApi()) {
      try {
        const resp = await accountsStore.update(id, { ...patch, appOrigin: window.location.origin });
        if (resp?.setupLink) console.log('[dev] re-sent setup link:', resp.setupLink);
        await reloadAccounts();
        return { ok: true, inviteResent: !!resp?.inviteResent };
      } catch (err) { return { error: err.message || 'Could not update the agent.' }; }
    }
    persistAccounts(accounts.map(a => a.id === id ? { ...a, ...patch } : a));
    return { ok: true };
  }

  // ── System admin: provision organizations ─────────────────────────────────
  async function createOrg({ name, seats, mlsId, adminName, adminEmail, trialDays, trialSeats }) {
    const n = (name || '').trim();
    if (!n) return 'Organization name is required.';
    if (!mlsId) return 'Please select the MLS this organization belongs to.';
    // Org names are unique WITHIN an MLS (case-insensitive) — same name may exist in another MLS.
    if (orgs.some(o => o.mlsId === mlsId && (o.name || '').toLowerCase() === n.toLowerCase())) return 'An organization with that name already exists in this MLS.';
    const ae = (adminEmail || '').trim();     // the admin's EMAIL is their login
    const an = (adminName || '').trim();      // real/display name → alias
    if (!ae) return "The organization admin's email is required — it is the login, and the setup link is sent there.";
    if (!EMAIL_RE.test(ae)) return 'Please enter a valid admin email address.';
    if (usingApi()) {
      try {
        const paid = Math.max(0, parseInt(seats, 10) || 0);
        const td = Math.max(0, parseInt(trialDays, 10) || 0);
        const ts = Math.max(0, parseInt(trialSeats, 10) || 0);
        // Admin is created password-less; the server emails them a one-time setup link.
        const resp = await orgStore.create({ name: n, mlsId, seats: paid, trialSeats: ts, trialDays: td, adminName: an, adminEmail: ae });
        if (resp?.setupLink) console.log('[dev] org admin setup link:', resp.setupLink);   // dev-only: server omits in prod
        await reloadOrgs();
        await reloadAccounts();   // so masterScope() can find the new org admin (holds the org's reminders)
        // Pull the new org admin's server-seeded (inherited) reminders into localStorage.
        try { hydrateDocs(await docStore.loadMine()); } catch {}
        return null;
      } catch (err) {
        return err.message || 'Could not create organization.';
      }
    }
    if (accounts.some(a => a.username.toLowerCase() === ae.toLowerCase())) return 'An account with this email already exists.';
    const td = Math.max(0, parseInt(trialDays, 10) || 0);
    const ts = Math.max(0, parseInt(trialSeats, 10) || 0);
    const paid = Math.max(0, parseInt(seats, 10) || 0);
    if (td > 0 && ts < 1) return 'A trial period needs at least 1 trial seat.';
    if (td === 0 && paid < 1) return 'With no trial, set at least 1 purchased seat.';
    const org = { id: genId('org'), name: n, seats: paid, trialSeats: ts, trialDays: td, mlsId, disabled: false, createdAt: new Date().toISOString() };
    const adminAcct = { id: genId('acc'), username: ae, orgId: org.id, password: (Math.random().toString(36).slice(2) + 'A1!'), admin: true, mustChangePassword: true, email: ae, alias: an, phone: '', licenseId: '', photo: '', history: [] };
    const sys = systemScope(accounts);
    seedTemplates(sys, mlsId);
    seedTemplates(mlsId, adminAcct.id);   // org inherits only the MLS's PUBLISHED reminders
    persistOrgs([...orgs, org]);
    persistAccounts([...accounts, adminAcct]);
    return null;
  }
  // Org admin picks a company banner from the header — resize and save it to the org.
  function onBannerPick(e) {
    const f = e.target.files && e.target.files[0];
    if (e.target) e.target.value = '';   // allow re-picking the same file
    if (!f || !agent?.orgId) return;
    resizeImage(f, 800, (dataUrl) => { updateOrg(agent.orgId, { banner: dataUrl }); });
  }
  async function updateOrg(id, patch) {
    if (usingApi()) {
      const body = {};
      if (patch.name != null) body.name = patch.name;
      if (patch.seats != null) body.seats = Math.max(0, parseInt(patch.seats, 10) || 0);
      if (patch.mlsId != null) body.mlsId = patch.mlsId;
      if (patch.trialDays != null) body.trialDays = Math.max(0, parseInt(patch.trialDays, 10) || 0);
      if (patch.trialSeats != null) body.trialSeats = Math.max(0, parseInt(patch.trialSeats, 10) || 0);
      if (patch.expiresAt !== undefined) body.expiresAt = patch.expiresAt || null;   // subscription end (null = clear)
      if (patch.banner !== undefined) body.banner = patch.banner || null;             // company banner (null = remove)
      // Reflect the change immediately (no flash of the old value while the PATCH +
      // reloadOrgs settle), then persist + refresh from the server. On failure (e.g.
      // a duplicate name → 409) reload to revert the optimistic change and surface it.
      setOrgs(prev => prev.map(o => o.id === id ? { ...o, ...body } : o));
      try {
        await orgStore.update(id, body);
        await reloadOrgs();
        return null;
      } catch (err) {
        await reloadOrgs().catch(() => {});
        return err.message || 'Could not update organization.';
      }
    }
    // localStorage mode: enforce org-name uniqueness WITHIN the org's effective MLS.
    if (patch.name != null) {
      const nn = String(patch.name).trim();
      const cur = orgs.find(o => o.id === id);
      const targetMls = patch.mlsId != null ? patch.mlsId : (cur && cur.mlsId);
      if (orgs.some(o => o.id !== id && o.mlsId === targetMls && (o.name || '').toLowerCase() === nn.toLowerCase())) return 'An organization with that name already exists in this MLS.';
    }
    persistOrgs(orgs.map(o => o.id === id
      ? { ...o,
          name: (patch.name != null ? patch.name : o.name),
          seats: (patch.seats != null ? Math.max(0, parseInt(patch.seats, 10) || 0) : o.seats),
          mlsId: (patch.mlsId != null ? patch.mlsId : o.mlsId),
          trialSeats: (patch.trialSeats != null ? Math.max(0, parseInt(patch.trialSeats, 10) || 0) : (o.trialSeats || 0)),
          trialDays: (patch.trialDays != null ? Math.max(0, parseInt(patch.trialDays, 10) || 0) : (o.trialDays || 0)),
          expiresAt: (patch.expiresAt !== undefined ? (patch.expiresAt || null) : (o.expiresAt || null)),
          banner: (patch.banner !== undefined ? (patch.banner || null) : (o.banner || null)) }
      : o));
    return null;
  }
  function setOrgDisabled(id, disabled) {
    // `disabled` isn't yet a server Org field (later phase) — localStorage path only.
    if (usingApi()) return;
    persistOrgs(orgs.map(o => o.id === id ? { ...o, disabled } : o));
  }
  function setOrgRemindersSuspendedById(orgId, suspended) {
    if (usingApi()) { orgStore.update(orgId, { remindersSuspended: !!suspended }).then(reloadOrgs).catch(() => {}); return; }
    persistOrgs(orgs.map(o => o.id === orgId ? { ...o, remindersSuspended: !!suspended } : o));
  }

  // ── System Org (direct member) management, run by the operator ─────────────
  // System Orgs never expire and have unlimited seats; each direct member instead
  // carries their own expiration. These mirror the org-admin handlers but are
  // scoped to an explicit orgId (the operator isn't a member of the org).
  async function sysOrgAddMember(orgId, { alias, email, phone, licenseId, mlsBrokerId, inviteSubject, inviteMessage }) {
    const em = (email || '').trim();
    if (!em) return 'Email is required — it is the login, and the setup link is sent there.';
    if (!EMAIL_RE.test(em)) return 'Please enter a valid email address.';
    const u = em;   // the email IS the login/username (one account per email)
    if (accounts.some(a => a.username.toLowerCase() === u.toLowerCase())) return 'An account with this email already exists.';
    const days = Math.max(0, parseInt(settings.signupTrialDays, 10) || 0);
    const expiresAt = days > 0 ? new Date(Date.now() + days * 86400000).toISOString() : null;
    if (usingApi()) {
      try {
        const resp = await accountsStore.create({ username: u, alias, email: em, orgId, admin: false, expiresAt, phone, licenseId, mlsBrokerId, appOrigin: window.location.origin, inviteSubject, inviteMessage });
        if (resp?.setupLink) console.log('[dev] member setup link:', resp.setupLink);
        await reloadAccounts();
        hydrateDocs(await docStore.loadMine());
        return null;
      } catch (err) { return err.message || 'Could not add member.'; }
    }
    const acct = { id: genId('acc'), username: u, orgId, email: em, password: (Math.random().toString(36).slice(2) + 'A1!'), mustChangePassword: true, expiresAt, createdAt: new Date().toISOString(), alias: (alias || '').trim(), phone: (phone || '').trim(), licenseId: (licenseId || '').trim(), mlsBrokerId: (mlsBrokerId || '').trim(), photo: '', history: [] };
    seedTemplates(orgId, acct.id);   // member inherits only the System Org's PUBLISHED reminders
    persistAccounts([...accounts, acct]);
    return null;
  }
  function sysOrgDeleteMembers(orgId, usernames) {
    const set = new Set(usernames || []);
    const idset = new Set(accounts.filter(a => a.orgId === orgId && set.has(a.username) && !a.system).map(a => a.id));
    if (!idset.size) return;
    purgeDataForScopes({ acctIds: [...idset] });   // wipe each removed member's contacts/properties/templates/files
    persistAccounts(accounts.filter(a => !idset.has(a.id)));
    if (usingApi()) Promise.all([...idset].map(id => accountsStore.remove(id).catch(() => {}))).then(reloadAccounts);
  }
  function sysOrgSetDisabled(orgId, usernames, disabled) {
    const at = new Date().toISOString();
    const set = new Set(usernames || []);
    const ids = accounts.filter(a => a.orgId === orgId && set.has(a.username) && !a.system && !!a.disabled !== disabled).map(a => a.id);
    persistAccounts(accounts.map(a => {
      if (a.orgId !== orgId || !set.has(a.username) || a.system) return a;
      if (!!a.disabled === disabled) return a;
      const history = [...(a.history || []), { action: disabled ? 'disabled' : 'enabled', at, by: agent.username }];
      return { ...a, disabled, history };
    }));
    if (usingApi() && ids.length) Promise.all(ids.map(id => accountsStore.update(id, { disabled }).catch(() => {}))).then(reloadAccounts);
    return null;
  }
  function sysOrgSetExpiry(orgId, username, expiresAt) {
    const acct = accounts.find(a => a.orgId === orgId && a.username === username && !a.system);
    persistAccounts(accounts.map(a => (a.orgId === orgId && a.username === username && !a.system) ? { ...a, expiresAt } : a));
    if (usingApi() && acct) accountsStore.update(acct.id, { expiresAt }).then(reloadAccounts).catch(() => {});
  }
  // System operator marks a direct agent's paid-subscription end (keeps them active past
  // the trial). null = not a paid subscriber.
  function sysOrgSetPaid(orgId, username, paidUntil) {
    const acct = accounts.find(a => a.orgId === orgId && a.username === username && !a.system);
    persistAccounts(accounts.map(a => (a.orgId === orgId && a.username === username && !a.system) ? { ...a, paidUntil } : a));
    if (usingApi() && acct) accountsStore.update(acct.id, { paidUntil }).then(reloadAccounts).catch(() => {});
  }
  function pushSysOrgReminder(orgId, reminderId, fromScope) {
    const src = fromScope || orgId;
    const targets = accounts.filter(a => a.orgId === orgId && !a.system).map(a => a.id);
    if (usingApi()) return apiPushReminder(src, reminderId, targets);
    let pushed = 0;
    targets.forEach(t => { if (pushReminder(src, t, reminderId)) pushed++; });
    return { pushed, total: targets.length };
  }
  // Build a complete, self-contained backup of an organization: the org record,
  // every (non-system) member account, and each member's data stores (contacts,
  // properties, assignments, templates, run history). Returned as a plain object
  // the System console serializes to a downloadable JSON file before deletion.
  function buildOrgBackup(orgId) {
    const org = orgById(orgs, orgId);
    if (!org) return null;
    const members = accounts.filter(a => a.orgId === orgId && !a.system);
    const data = {};
    members.forEach(m => {
      const perAccount = {};
      ORG_DATA_BASES.forEach(base => {
        const raw = localStorage.getItem(scopedKey(base, m.id));
        if (raw != null) {
          try { perAccount[base] = JSON.parse(raw); } catch { perAccount[base] = raw; }
        }
      });
      data[m.id] = perAccount;
    });
    return {
      format: 'ibetteragent-org-backup',
      version: 1,
      exportedAt: new Date().toISOString(),
      org,
      accounts: members,   // full records (incl. credentials) — this is a restore-grade backup
      data,
    };
  }
  function deleteOrg(id) {
    if (usingApi()) { orgStore.remove(id).then(reloadOrgs).catch(() => {}); return; }
    // Purge every member's scoped data stores + the org's own, so nothing is orphaned.
    const memberIds = accounts.filter(a => a.orgId === id && !a.system).map(a => a.id);
    purgeDataForScopes({ acctIds: memberIds, orgIds: [id] });
    persistAccounts(accounts.filter(a => a.orgId !== id));
    persistOrgs(orgs.filter(o => o.id !== id));
  }
  // Deleting an MLS removes EVERYTHING under it: every org on that MLS (its System Org
  // for direct signups + every provisioned org), all their members, all of those
  // members' data, the orgs' own scope data, and the MLS's own scope data + files.
  // The catalog entry itself is removed by SupportedMls (which owns the catalog state).
  // Accepts a single id or an array (bulk delete) — computed in one pass so concurrent
  // deletes don't fight over stale state.
  function deleteMls(mlsId) {
    const ids = Array.isArray(mlsId) ? mlsId : [mlsId];
    const mlsSet = new Set(ids.filter(Boolean));
    if (!mlsSet.size) return;
    if (usingApi()) {
      // Server cascades org/user FK to null on MLS delete; refresh catalog + orgs.
      Promise.all([...mlsSet].map(id => mlsStore.remove(id).catch(() => {})))
        .then(() => { reloadMls(); reloadOrgs(); });
      return;
    }
    const orgIds = orgs.filter(o => mlsSet.has(o.mlsId)).map(o => o.id);
    const orgIdSet = new Set(orgIds);
    const memberIds = accounts.filter(a => a.orgId && orgIdSet.has(a.orgId) && !a.system).map(a => a.id);
    purgeDataForScopes({ acctIds: memberIds, orgIds, mlsIds: [...mlsSet] });
    persistAccounts(accounts.filter(a => !(a.orgId && orgIdSet.has(a.orgId))));
    persistOrgs(orgs.filter(o => !orgIdSet.has(o.id)));
  }
  // Nuke EVERYTHING below System: every MLS, every org, every agent/admin, and all of
  // their contacts/properties/reminders/files. Keeps only System Admin accounts (and the
  // System's own reminders/settings). Used by the Settings → Clean Data button.
  async function cleanAllData() {
    // API mode: the central DB is the source of truth, so the wipe must happen
    // server-side (the localStorage-only path below would just clear the mirror and
    // everything would reload from the server). Keeps only System Admin(s).
    if (usingApi()) {
      try {
        await api.post('/api/admin/clean-data', {});
        purgeOrphanScopes();   // clear the local mirror of the now-deleted tenant data
        await Promise.all([reloadMls(), reloadOrgs(), reloadAccounts()]);
      } catch (e) { console.error('Clean data failed:', e?.message || e); }
      resetSystemView();       // the MLS/org we were drilled into is gone — go top-level
      return;
    }
    localStorage.setItem('ira_mls__system', JSON.stringify([]));   // empty the MLS catalog
    persistOrgs([]);                                                // drop every org
    persistAccounts(accounts.filter(a => a.system));                // keep only System Admin(s)
    purgeOrphanScopes();   // removes every now-orphaned scope: contacts, templates, files, …
    resetSystemView();
  }
  // After a wipe, drop any MLS/org drill-in so the System console doesn't try to
  // render data that no longer exists.
  function resetSystemView() {
    setManageMlsId(null);
    setManageSysOrgId(null);
    setSystemTab('mls');
    setActiveSection('system');
  }
  // Restore an organization from a backup produced by buildOrgBackup: re-creates the
  // org record, its member accounts, and each member's data stores. Refuses to
  // overwrite anything that already exists (the org must have been deleted first).
  function restoreOrgBackup(backup) {
    if (!backup || backup.format !== 'ibetteragent-org-backup') return 'That file is not an iBetterAgent organization backup.';
    const org = backup.org;
    const accts = Array.isArray(backup.accounts) ? backup.accounts : [];
    if (!org || !org.id) return 'The backup is missing its organization record.';
    if (orgs.some(o => o.id === org.id)) return `“${org.name}” is already present. Delete it before restoring.`;
    if (orgs.some(o => (o.name || '').toLowerCase() === (org.name || '').toLowerCase())) return `An organization named “${org.name}” already exists.`;
    const existingIds = new Set(accounts.map(a => a.id));
    const existingNames = new Set(accounts.map(a => a.username.toLowerCase()));
    for (const a of accts) {
      if (existingIds.has(a.id)) return `Account "${a.username}" (id ${a.id}) already exists — cannot restore.`;
      if (existingNames.has((a.username || '').toLowerCase())) return `Username "${a.username}" is already taken — cannot restore.`;
    }
    // Write back each account's data stores.
    const data = backup.data || {};
    Object.keys(data).forEach(accId => {
      const stores = data[accId] || {};
      ORG_DATA_BASES.forEach(base => {
        if (stores[base] != null) {
          const v = stores[base];
          localStorage.setItem(scopedKey(base, accId), typeof v === 'string' ? v : JSON.stringify(v));
        }
      });
    });
    persistOrgs([...orgs, org]);
    persistAccounts([...accounts, ...accts]);
    return null;
  }
  // System Admin resets an org admin's password to a temporary one and forces a
  // change at their next login.
  // Email the admin a password-reset/setup link (no temp password). They set their own
  // password from the link and are signed in on completion.
  async function resetOrgAdminPassword(accountId) {
    if (usingApi()) {
      try {
        const data = await api.post(`/api/users/${accountId}/reset-link`, { appOrigin: window.location.origin });
        if (data.setupLink) console.log('[dev] reset link:', data.setupLink);   // server omits in prod
        return { ok: true, sent: !!data.sent };
      } catch (err) {
        return { error: err.message || 'Could not send the reset link.' };
      }
    }
    // localStorage fallback (dev-local, non-API): mark must-change so next login forces it.
    persistAccounts(accounts.map(a => a.id === accountId ? { ...a, mustChangePassword: true } : a));
    return { ok: true, sent: false };
  }
  // The signed-in user sets a new password (used for the forced first-login change).
  async function changeOwnPassword(newPassword) {
    const p = (newPassword || '').trim();
    if (!p) return 'Password is required.';
    const pwErr = passwordError(p); if (pwErr) return pwErr;
    if (usingApi()) {
      try {
        const user = await authStore.changePassword(p);
        ingestApiUser(user);
        setActiveSection(user.system ? 'system' : (user.admin ? 'reminder-templates' : 'contacts'));
        return null;
      } catch (err) {
        return err.message || 'Could not change password.';
      }
    }
    if (p === agent.password) return 'Please choose a new password, different from the temporary one.';
    persistAccounts(accounts.map(a => a.id === agent.id ? { ...a, password: p, mustChangePassword: false } : a));
    setActiveSection(agent.system ? 'system' : (agent.admin ? 'reminder-templates' : 'contacts'));
    return null;
  }

  // ── Routing ────────────────────────────────────────────────────────────────
  // Email verification for a public self-sign-up (?verify=<token>). On success the
  // effect clears verifyToken and the app renders normally (now signed in).
  if (verifyToken) {
    return (
      <div className="login-screen">
        <div className="login-card" style={{ textAlign: 'center' }}>
          <div className="login-brand"><span className="login-logo"><Icon name="brand" size={22} /></span> iBetterAgent</div>
          {verifyState.loading ? (
            <p style={{ color: '#64748b', margin: '18px 0' }}>Verifying your email and setting up your account…</p>
          ) : (
            <>
              <div className="login-error">⚠ {verifyState.error}</div>
              <button type="button" className="login-btn" style={{ marginTop: 14 }}
                onClick={() => { window.history.replaceState({}, '', window.location.pathname); setVerifyToken(null); setVerifyState({ loading: false, error: '' }); setShowAuth(true); }}>
                Back to sign up
              </button>
            </>
          )}
        </div>
      </div>
    );
  }
  if (resetToken) {
    if (resetInfo.loading) {
      return <div className="app-boot" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', color: '#64748b' }}>Loading…</div>;
    }
    return <ResetPassword token={resetToken} valid={resetInfo.valid} username={resetInfo.username} onSetPassword={setPasswordByToken} onDone={finishReset} />;
  }

  // Public agent landing page — /agent/<slug>. Rendered before any auth/session
  // resolution so it works with no login (and doesn't flash the app shell).
  const landingMatch = window.location.pathname.match(/^\/agent\/([^/]+)\/?$/);
  if (landingMatch) {
    return <AgentLanding slug={decodeURIComponent(landingMatch[1])} />;
  }

  // Public review-submission page — /review/<token>. No login (the reviewer is a contact).
  const reviewMatch = window.location.pathname.match(/^\/review\/([^/]+)\/?$/);
  if (reviewMatch) {
    return <ReviewSubmit token={decodeURIComponent(reviewMatch[1])} />;
  }

  // API-auth: hold the UI while we resolve the session from the server, so we
  // don't flash the login/landing screen before /me returns.
  if (apiBooting && !agent) {
    return <div className="app-boot" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', color: '#64748b' }}>Loading…</div>;
  }

  if (!agent) {
    // A single-use invite link that's already used / expired / invalid: show a clear
    // message instead of the sign-up form (the link can't create another account).
    if (inviteUnusable) {
      const msg = inviteInfo.invalid
        ? 'This invitation link is invalid.'
        : inviteInfo.used
          ? 'This invitation link has already been used. Each invite works once — please ask your administrator for a new link.'
          : 'This invitation link has expired. Please ask your administrator for a new link.';
      return (
        <div className="login-screen">
          <div className="login-card" style={{ textAlign: 'center' }}>
            <div className="login-brand"><span className="login-logo"><Icon name="brand" size={22} /></span><span className="login-title">iBetterAgent</span></div>
            <div className="login-info" style={{ marginTop: 12 }}>🔗 {msg}</div>
            <button className="rt-save-btn" style={{ marginTop: 16 }} onClick={() => { window.location.href = '/'; }}>Go to sign in</button>
          </div>
        </div>
      );
    }
    return showAuth
      ? <Login onLogin={login} onRegister={register} onForgot={requestReset} onBack={() => setShowAuth(false)} mlsCatalog={loadSystemMls()} initialMode={signupIntent ? 'signup' : 'login'} allowSignup={settings.publicSignups || signupIntent || settings.devMode} lockedMlsId={lockedMls?.id} lockedMlsName={lockedMls?.name} lockedEmail={lockedEmail}
          lockedOrgId={lockedOrg?.id} lockedOrgName={lockedOrg?.name} lockedOrgMlsName={lockedOrg ? (lockedOrg.mlsName || loadSystemMls().find(m => m.id === lockedOrg.mlsId)?.name || '') : ''} />
      : <Landing onGetStarted={() => setShowAuth(true)} />;
  }

  // Forced password change on first login (or after an admin reset).
  if (agent.mustChangePassword) {
    return <ForcePasswordChange username={agent.username} onSet={changeOwnPassword} onLogout={logout} />;
  }

  const displayName = agent.alias || agent.username;
  const initial = displayName.trim().charAt(0).toUpperCase();

  // ── System Admin console ──
  if (agent.system) {
    const manageOrg = manageSysOrgId ? orgById(orgs, manageSysOrgId) : null;
    // Every org stores its reminder data under org.id (uniform — system or regular).
    const manageOrgReminderScope = manageOrg ? manageOrg.id : null;
    const manageMembers = manageOrg ? accounts.filter(a => a.orgId === manageOrg.id && !a.system) : [];
    const manageMls = manageMlsId ? loadSystemMls().find(m => m.id === manageMlsId) : null;
    // Everyone (non-system) across every org licensed under this MLS — used for the
    // MLS-level "Message Agents" broadcast (e.g. announcing newly uploaded files).
    const mlsOrgIds = manageMls ? orgs.filter(o => o.mlsId === manageMls.id).map(o => o.id) : [];
    const mlsAgents = manageMls ? accounts.filter(a => !a.system && mlsOrgIds.includes(a.orgId)) : [];
    const drillIn = manageOrg || manageMls;
    return (
      <div className="app">
        {usingApi() && <SyncStatus />}
        <header className="app-header">
          <div className="header-brand">
            <span className="header-logo"><Icon name="brand" size={22} /></span>
            <span className="header-title">iBetterAgent</span>
            <span className="header-sys-badge">System</span>
          </div>
          <div className="header-actions">
            <button className="header-logout" onClick={logout}><Icon name="logout" size={16} /> Log Out</button>
          </div>
        </header>
        {!drillIn && (
          /* Desktop: inline tabs. Mobile: hidden — shown as a native bottom bar below. */
          <nav className="sys-tabs sys-subtabs">
            {SYS_TABS.map(t => (
              <button key={t.id} className={`sys-tab ${systemTab === t.id ? 'active' : ''}`} onClick={() => setSystemTab(t.id)}><Icon name={t.icon} size={16} /> {t.label}</button>
            ))}
          </nav>
        )}
        <div className="app-body app-body-system">
          <main className="main-content">
            {(manageMls && !manageOrg) ? (
              <>
              <div className="sa-container">
                <button className="sa-restore-btn sa-back-btn" onClick={() => setManageMlsId(null)}>← Back to Supported MLS</button>
                <div className="sa-header">
                  <div>
                    <h2>{manageMls.name}</h2>
                    <p>Manage this MLS's organizations, reminder templates, and market-report files. (System → MLS → Org → Agent.)</p>
                  </div>
                </div>
                {/* Desktop: inline tabs. Mobile: hidden — shown as a native bottom bar below. */}
                <nav className="sys-tabs sys-subtabs">
                  {MLS_SUBTABS.map(t => (
                    <button key={t.id} className={`sys-tab ${manageMlsTab === t.id ? 'active' : ''}`} onClick={() => setManageMlsTab(t.id)}><Icon name={t.icon} size={16} /> {t.label}</button>
                  ))}
                </nav>
                {manageMlsTab === 'orgs' ? (
                  <SystemAdmin orgs={orgs} accounts={accounts} mlsCatalog={loadSystemMls()} mlsId={manageMls.id}
                    onCreateOrg={createOrg} onUpdateOrg={updateOrg} onSetOrgDisabled={setOrgDisabled}
                    onSetOrgExpiry={(id, iso) => updateOrg(id, { expiresAt: iso })}
                    onDeleteOrg={deleteOrg} onBuildBackup={buildOrgBackup} onRestore={restoreOrgBackup}
                    onResetAdminPassword={resetOrgAdminPassword}
                    onManage={(id) => { setManageSysOrgId(id); setManageSysTab('reminders'); }} />
                ) : manageMlsTab === 'reminders' ? (
                  <ReminderTemplates key={`mls-${manageMls.id}-${mlsRev}`} scope={manageMls.id} isAdmin
                    onPush={(id) => pushMlsReminder(manageMls.id, id)} pushUnit="organization"
                    onPropagateReminder={(id) => propagateMlsReminder(manageMls.id, id)}
                    fileKeys={{ ownKeys: [`mls:${manageMls.id}`], ancestors: [] }} />
                ) : manageMlsTab === 'files' ? (
                  <div className="mls-files-panel" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 18 }}>
                    <div className="mls-files-title"><Icon name="files" size={16} /> Market Report files for <strong>{manageMls.name}</strong></div>
                    <p className="mls-files-hint">Public files are available to every org and agent in this MLS when they build reminders — stored once, never copied.</p>
                    <FileManager ownerKey={`mls:${manageMls.id}`} />
                  </div>
                ) : manageMlsTab === 'process' ? (
                  <Process accounts={mlsAgents} orgs={orgs} mlsList={loadSystemMls()} settings={settings}
                    level="mls" scopeName={manageMls.name} mlsId={manageMls.id}
                    onSetMlsProcess={(v) => updateSystemMls(manageMls.id, { processSuspended: v })} />
                ) : manageMlsTab === 'settings' ? (
                  <MlsSettings mls={manageMls} settings={settings}
                    onUpdate={(patch) => updateSystemMls(manageMls.id, patch)} />
                ) : (
                  <MessageAgents agents={mlsAgents} orgName={manageMls.name}
                    subtitle={`Send a one-off email to every agent across all organizations in ${manageMls.name} (e.g. to announce newly uploaded files). This isn't saved — it sends now.`}
                    emptyMsg="No agents in this MLS yet. They appear once organizations add agents (or agents sign up directly)."
                    fileKeys={{ ownKeys: [`mls:${manageMls.id}`], ancestors: [] }} signature={agent.signature || ''} />
                )}
              </div>
              {/* Native-style mobile bottom navigation for the MLS sub-tabs. */}
              <nav className="mobile-tabbar">
                {MLS_SUBTABS.map(t => (
                  <button key={t.id} className={`tabbar-item ${manageMlsTab === t.id ? 'active' : ''}`} onClick={() => setManageMlsTab(t.id)}>
                    <span className="tabbar-icon"><Icon name={t.icon} size={18} /></span>
                    <span className="tabbar-label">{t.short}</span>
                  </button>
                ))}
              </nav>
              </>
            ) : manageOrg ? (
              <>
              <div className="sa-container">
                <button className="sa-restore-btn sa-back-btn" onClick={() => setManageSysOrgId(null)}>← Back to {manageMls ? manageMls.name : 'Organizations'}</button>
                <div className="sa-header">
                  <div>
                    <h2>{manageOrg.name}</h2>
                    <p>{manageOrg.systemOrg ? 'System Org · never expires. Manage its reminder templates and direct members (each member has an individual expiration).' : 'Licensed org. Reminder templates are the org admin\'s master copies; agents inherit their public ones.'}</p>
                  </div>
                </div>
                {/* Desktop: inline tabs. Mobile: hidden — shown as a native bottom bar below. */}
                <nav className="sys-tabs sys-subtabs">
                  {orgSubtabsFor(manageOrg).map(t => (
                    <button key={t.id} className={`sys-tab ${manageSysTab === t.id ? 'active' : ''}`} onClick={() => setManageSysTab(t.id)}><Icon name={t.icon} size={16} /> {t.label}</button>
                  ))}
                </nav>
                {manageSysTab === 'reminders' ? (
                  <ReminderTemplates key={`sysorg-${manageOrgReminderScope}-${mlsRev}`} scope={manageOrgReminderScope} isAdmin
                    suspendScope="org" suspended={!!manageOrg.remindersSuspended}
                    onToggleSuspend={(v) => setOrgRemindersSuspendedById(manageOrg.id, v)}
                    onPush={(id) => pushSysOrgReminder(manageOrg.id, id, manageOrgReminderScope)}
                    onPropagateReminder={(id) => propagateOrgReminder(manageOrg.id, id)}
                    fileKeys={{ ownKeys: [`org:${manageOrg.id}`], ancestors: manageOrg.mlsId ? [`mls:${manageOrg.mlsId}`] : [] }} />
                ) : manageSysTab === 'files' ? (
                  <FilesLibrary ownerKey={`org:${manageOrg.id}`} ancestors={manageOrg.mlsId ? [`mls:${manageOrg.mlsId}`] : []}
                    subtitle="Upload files for this organization. Mark one Public to share it (read-only) with its agents; the MLS's files shared with this org appear below and can be passed on to agents." />
                ) : manageSysTab === 'invite' ? (
                  <InviteAgents orgId={manageOrg.id} orgName={manageOrg.name} />
                ) : manageSysTab === 'message' ? (
                  <MessageAgents agents={manageMembers.filter(a => !a.admin)} orgName={manageOrg.name}
                    fileKeys={{ ownKeys: [`org:${manageOrg.id}`], ancestors: manageOrg.mlsId ? [`mls:${manageOrg.mlsId}`] : [] }} signature={agent.signature || ''} />
                ) : (
                  <Admin accounts={manageMembers} currentUsername={agent.username} orgName={manageOrg.name}
                    mlsName={loadSystemMls().find(m => m.id === manageOrg.mlsId)?.name || ''}
                    heading="Manage Agents" addLabel="Add Agent"
                    onAddUser={(d) => sysOrgAddMember(manageOrg.id, d)}
                    onUpdateUser={updateMember}
                    onDeleteUsers={(u) => sysOrgDeleteMembers(manageOrg.id, u)}
                    onSetDisabled={(u, dis) => sysOrgSetDisabled(manageOrg.id, u, dis)}
                    onResetPassword={(id) => resetOrgAdminPassword(id)}
                    onImpersonate={(u, note) => impersonate(manageMembers.find(m => m.username === u)?.id, note)}
                    onSetExpiry={manageOrg.systemOrg ? ((u, iso) => sysOrgSetExpiry(manageOrg.id, u, iso)) : undefined}
                    onSetPaid={manageOrg.systemOrg ? ((u, iso) => sysOrgSetPaid(manageOrg.id, u, iso)) : undefined}
                    canImpersonate={agent.system}
                    hideAdminGrant />
                )}
              </div>
              {/* Native-style mobile bottom navigation for the System-Org sub-tabs. */}
              <nav className="mobile-tabbar">
                {orgSubtabsFor(manageOrg).map(t => (
                  <button key={t.id} className={`tabbar-item ${manageSysTab === t.id ? 'active' : ''}`} onClick={() => setManageSysTab(t.id)}>
                    <span className="tabbar-icon"><Icon name={t.icon} size={18} /></span>
                    <span className="tabbar-label">{t.short}</span>
                  </button>
                ))}
              </nav>
              </>
            ) : systemTab === 'mls' ? (
              <SupportedMls scope="system" onManageMls={(id) => { setManageMlsId(id); setManageMlsTab('orgs'); }}
                onDeleteMls={deleteMls}
                accounts={accounts} orgs={orgs}
                apiMode={usingApi()} catalog={usingApi() ? loadSystemMls() : null}
                onCreate={createMls} onUpdate={updateMlsApi}
                onSaved={() => {
                  if (usingApi()) { reloadMls(); return; }
                  setMlsRev(r => r + 1);
                  const { orgs: ensured, changed } = ensureSystemOrgs(orgs, loadSystemMls());
                  if (changed) persistOrgs(ensured);
                  const sys = systemScope(accounts);
                  if (!sys) return;
                  loadSystemMls().forEach(m => { seedTemplates(sys, m.id); });
                  ensured.filter(o => o.systemOrg).forEach(o => {
                    seedTemplates(sys, o.id);
                    seedTemplates(o.mlsId || sys, o.id);
                  });
                }} />
            ) : systemTab === 'process' ? (
              <>
                <ServerRunStatus />
                <Process accounts={accounts} orgs={orgs} mlsList={loadSystemMls()} settings={settings} level="system"
                  onToggleSystemSuspend={(v) => updateSettings({ processSuspended: v })} />
              </>
            ) : systemTab === 'settings' ? (
              <SystemSettings settings={settings} onUpdate={updateSettings}
                onCleanData={cleanAllData}
                impact={{
                  mls: loadSystemMls().length,
                  orgs: orgs.length,
                  agents: accounts.filter(a => !a.system).length,
                }} />
            ) : (
              <ReminderTemplates key={`sys-${agent.id}`} scope={agent.id} isAdmin systemLevel showAllBuiltins onPush={pushReminderDown} pushUnit="MLS"
                onPropagateReminder={(id) => propagateSystemReminder(agent.id, id)}
                fileKeys={{ ownKeys: [], publicKeys: loadSystemMls().map(m => `mls:${m.id}`) }} />
            )}
          </main>
        </div>
        {/* Native-style mobile bottom navigation for the top-level System tabs. */}
        {!drillIn && (
          <nav className="mobile-tabbar">
            {SYS_TABS.map(t => (
              <button key={t.id} className={`tabbar-item ${systemTab === t.id ? 'active' : ''}`} onClick={() => setSystemTab(t.id)}>
                <span className="tabbar-icon"><Icon name={t.icon} size={18} /></span>
                <span className="tabbar-label">{t.short}</span>
              </button>
            ))}
          </nav>
        )}
      </div>
    );
  }

  const org = agent.orgId ? orgById(orgs, agent.orgId) : null;
  const orgAccounts = agent.orgId ? accounts.filter(a => a.orgId === agent.orgId && !a.system) : [agent];
  const usedSeats = agent.orgId ? seatsUsed(accounts, agent.orgId) : 0;

  // Org-level trial status — shown to every member while the org is on its FREE trial.
  // A purchased subscription (subscription date in the future) supersedes the trial
  // window, so a licensed org never shows the trial banner even if it's still inside
  // its original trialDays window.
  const orgLicensed = orgSubscribed(org);
  const inTrial = orgInTrial(org) && !orgLicensed;
  const trialLeft = inTrial ? orgTrialDaysLeft(org) : null;
  const orgHasPaidSeats = !!(org && Number(org.seats) > 0);
  // Over capacity: more active members than seats → reminder sending paused org-wide.
  const overCapacity = org ? orgOverCapacity(accounts, org) : false;

  // Direct (System Org) members have an individual term with iBetterAgent.
  const myDaysLeft = (!agent.system && agent.expiresAt && !userPaid(agent)) ? userDaysLeft(agent) : null;

  // The MLS an org belongs to is provisioned by the System Admin. Independent
  // agents manage their own MLS list.
  const orgMls = org && org.mlsId ? loadSystemMls().find(m => m.id === org.mlsId) : null;
  const orgMlsName = orgMls ? orgMls.name : null;

  let navItems;
  if (agent.admin) {
    navItems = [
      { id: 'reminder-templates', label: t('nav.templates'), icon: 'reminder-templates' },
      { id: 'files', label: t('nav.files'), icon: 'files' },
      { id: 'admin', label: t('nav.agents'), icon: 'admin' },
      { id: 'invite', label: 'Invite', icon: 'invite' },
      { id: 'message-agents', label: t('nav.messageAgents'), icon: 'message-agents' },
      { id: 'account', label: 'Account', icon: 'organizations' },
    ];
  } else {
    navItems = NAV_ITEMS.map(i => ({ id: i.id, icon: i.icon, label: t(i.labelKey) }));
    // Independent agents manage their own MLS list (kept before Run Reminders).
    // (Message Contacts is reached from the Contacts screen header, not the nav.)
    if (agent.independent) navItems.splice(navItems.length - 1, 0, { id: 'supported-mls', label: t('nav.mls'), icon: 'supported-mls' });
    // Reviews (client testimonials for the public profile), then Files, at the end.
    navItems.push({ id: 'reviews', label: 'Reviews', icon: 'star' });
    navItems.push({ id: 'files', label: t('nav.files'), icon: 'files' });
  }

  // File-library context for this user: where their uploads go, and which parent
  // levels' PUBLIC files they inherit. (Files are referenced by reminders, not copied.)
  const ownerMlsId = org ? org.mlsId : (agent.mls || null);
  const fileOwnerKey = agent.admin ? `org:${agent.orgId}` : `agent:${agent.id}`;
  // Ordered ancestor chain (nearest parent first) so file visibility honors the
  // per-level relay: an MLS file reaches an agent only if the org relayed it on.
  const fileAncestors = agent.admin
    ? (org && org.mlsId ? [`mls:${org.mlsId}`] : [])
    : [agent.orgId ? `org:${agent.orgId}` : null, ownerMlsId ? `mls:${ownerMlsId}` : null].filter(Boolean);
  // Picker keys (own files seen in full + inherited/relayed files) for the reminder editor.
  const fileKeys = { ownKeys: [fileOwnerKey], ancestors: fileAncestors };

  // 'message-contacts' is a valid section for agents even though it's not in the nav
  // (it's opened from the Contacts screen header).
  // 'getting-started' has no nav tab (reached from the Profile link and as the
  // first-login landing), so it's whitelisted here for non-admin agents.
  const allowedIds = [...navItems.map(i => i.id), ...(!agent.admin ? ['message-contacts', 'getting-started'] : [])];
  const effectiveSection = (activeSection === 'profile' || allowedIds.includes(activeSection))
    ? activeSection
    : navItems[0].id;

  // Mobile bottom tab bar shows every destination at once (compact labels), no "More".
  const goTo = (id) => setActiveSection(id);

  const dataScope = agent.id;
  const masterId = masterScope(accounts, agent.orgId);
  // "Getting Started" graduates away once all onboarding steps are done — the evergreen
  // "Agent Quick Start Guide" remains as the reference. Admins never see the checklist.
  const setupDone = !agent.admin && setupProgress(agent, dataScope).allDone;
  const supportLink = <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>;

  // Org members can import (their org belongs to an MLS). Independent agents must
  // first pick a Supported MLS in their own list.
  let importEnabled;
  if (agent.independent) {
    let ownMls = [];
    try { ownMls = JSON.parse(localStorage.getItem(scopedKey('ira_mls', agent.id)) || '[]'); } catch {}
    importEnabled = !!agent.mls && ownMls.some(m => m.id === agent.mls);
  } else {
    importEnabled = true;
  }

  return (
    <div className="app">
      {usingApi() && <SyncStatus />}
      {impersonator && (
        <div className="impersonation-bar">
          <span className="impersonation-text">
            <Icon name="shield-check" size={16} /> Support access — you are signed in as <strong>{displayName}</strong>. Changes affect this user's account.
          </span>
          <button className="impersonation-exit" onClick={exitImpersonation}>Exit impersonation</button>
        </div>
      )}
      <header className="app-header">
        <div className="header-brand">
          <span className="header-logo"><Icon name="brand" size={22} /></span>
          <span className="header-title">iBetterAgent</span>
          {org && !org.systemOrg
            ? <span className={`header-org ${org.banner ? 'has-banner' : ''}`}>{org.name}</span>
            : agent.independent && <span className="header-org">Independent</span>}
          {org && !org.systemOrg && (agent.admin || org.banner) && (
          <div className="header-banner-slot">
            {agent.admin && <input ref={bannerInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onBannerPick} />}
            {org.banner ? (
              agent.admin ? (
                <span className="header-banner-wrap">
                  <button type="button" className="header-banner-btn" title="Change company banner" onClick={() => bannerInputRef.current && bannerInputRef.current.click()}>
                    <img className="header-banner-img" src={org.banner} alt="Company banner" />
                  </button>
                  <button type="button" className="header-banner-x" title="Remove banner" onClick={() => updateOrg(org.id, { banner: null })}>✕</button>
                </span>
              ) : (
                <img className="header-banner-img" src={org.banner} alt="Company banner" />
              )
            ) : (
              agent.admin && (
                <button type="button" className="header-banner-add" onClick={() => bannerInputRef.current && bannerInputRef.current.click()}>
                  <Icon name="organizations" size={14} /> Add company banner
                </button>
              )
            )}
          </div>
          )}
        </div>
        <div className="header-actions">
          <button
            className={`header-user ${activeSection === 'profile' ? 'active' : ''}`}
            title={t('header.profile')}
            onClick={() => setActiveSection('profile')}
          >
            {agent.photo
              ? <img className="header-avatar" src={agent.photo} alt={displayName} />
              : <span className="header-avatar header-avatar-initial">{initial}</span>}
            <span className="header-user-name">{displayName}</span>
          </button>
        </div>
      </header>

      <div className="app-body">
        <nav className="left-pane">
          {navItems.map(item => (
            <button
              key={item.id}
              className={`nav-item ${effectiveSection === item.id ? 'active' : ''}`}
              onClick={() => setActiveSection(item.id)}
            >
              <span className="nav-icon"><Icon name={item.icon} size={20} /></span>
              <span className="nav-label">{item.label}</span>
            </button>
          ))}
        </nav>

        <main className="main-content">
          {inTrial && !overCapacity && (
            <div className={`trial-banner ${trialLeft <= 7 ? 'warn' : ''}`}>
              {trialLeft <= 7
                ? <>⏳ Your organization's free trial ends in {trialLeft} day{trialLeft === 1 ? '' : 's'}{orgHasPaidSeats ? '.' : <> — purchase seats (contact iBetterAgent at {supportLink}) to keep access.</>}</>
                : `🎁 Free trial — ${trialLeft} day${trialLeft === 1 ? '' : 's'} of access left${orgHasPaidSeats ? ' (purchased seats active afterward).' : '. Upgrade anytime to continue after the trial.'}`}
            </div>
          )}
          {overCapacity && (
            <div className="trial-banner warn">
              {agent.admin
                ? `⛔ Over capacity — ${usedSeats} active members but only ${orgEffectiveSeats(org)} seat${orgEffectiveSeats(org) === 1 ? '' : 's'}. Reminder sending is paused org-wide. Disable members on the Users page (or purchase more seats) to resume.`
                : '⛔ Reminders are paused — your organization is over its seat limit. Your admin needs to free up seats to resume.'}
            </div>
          )}
          {myDaysLeft != null && (
            <div className={`trial-banner ${myDaysLeft <= 7 ? 'warn' : ''}`}>
              {myDaysLeft <= 7
                ? <>⏳ Your iBetterAgent subscription ends in {myDaysLeft} day{myDaysLeft === 1 ? '' : 's'} — contact iBetterAgent at {supportLink} to renew and keep access.</>
                : `🎁 iBetterAgent subscription active — ${myDaysLeft} day${myDaysLeft === 1 ? '' : 's'} of access left.`}
            </div>
          )}
          {/* Dismissible "finish setup" nudge — selling agents only, hidden on the
              checklist itself; self-hides once setup is complete or dismissed. */}
          {!agent.admin && effectiveSection !== 'getting-started' && (
            <SetupBanner agent={agent} scope={dataScope} onOpen={() => setActiveSection('getting-started')} />
          )}
          {effectiveSection === 'getting-started' && !agent.admin ? (
            <GettingStarted agent={agent} scope={dataScope} onNavigate={setActiveSection} />
          ) : effectiveSection === 'profile' ? (
            <Profile agent={agent} onSave={saveAgent} onLogout={logout}
              mlsEditable={!!agent.independent} orgMlsName={orgMlsName}
              onGettingStarted={(!agent.admin && !setupDone) ? () => setActiveSection('getting-started') : undefined} />
          ) : effectiveSection === 'admin' && agent.admin ? (
            <Admin accounts={orgAccounts} currentUsername={agent.username} heading="Manage Agents" addLabel="Add Agent"
              orgName={org?.name} mlsName={orgMlsName} seats={org ? orgEffectiveSeats(org) : undefined} usedSeats={usedSeats}
              onAddUser={adminAddUser} onUpdateUser={updateMember} onDeleteUsers={adminDeleteUsers} onSetDisabled={adminSetDisabled}
              onResetPassword={(id) => resetOrgAdminPassword(id)}
              onImpersonate={(username, note) => impersonate(resolveInOrg(username)?.id, note)}
              canImpersonate={agent.system} />
          ) : effectiveSection === 'invite' && agent.admin ? (
            <InviteAgents trialDays={effectiveTrialDays(orgMls, settings)} orgId={agent.orgId} orgName={org?.name} />
          ) : effectiveSection === 'message-agents' && agent.admin ? (
            <MessageAgents agents={orgAccounts.filter(a => !a.admin)} orgName={org?.name} fileKeys={fileKeys} signature={agent.signature || ''} />
          ) : effectiveSection === 'account' && agent.admin ? (
            (() => {
              const seatsPurchased = org ? (Number(org.seats) || 0) : 0;
              const seatsCap = org ? orgEffectiveSeats(org) : 0;
              const capLabel = seatsCap === Infinity ? 'Unlimited' : seatsCap;
              const status = !org ? '—'
                : orgLicensed ? 'Licensed'
                : orgBlockedReason(org) === 'expired' ? 'Expired'
                : orgBlockedReason(org) === 'suspended' ? 'Suspended'
                : inTrial ? 'Free trial'
                : seatsPurchased > 0 ? 'Licensed' : 'Expired';
              const statusClass = status === 'Licensed' ? 'ok' : (status === 'Free trial' ? 'trial' : 'off');
              const subDate = org && org.expiresAt ? new Date(org.expiresAt) : null;
              const fmt = (d) => d && !isNaN(d.getTime()) ? d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : '—';
              return (
                <div className="account-panel">
                  <div className="account-head">
                    <h2><Icon name="organizations" size={20} style={{ verticalAlign: '-4px' }} /> Account</h2>
                    <p>Your organization's iBetterAgent subscription. To make changes, contact iBetterAgent at {supportLink}{orgMlsName ? ` (${orgMlsName})` : ''}.</p>
                  </div>
                  <div className="account-grid">
                    <div className="account-row"><span className="account-label">Organization</span><span className="account-value">{org?.name || '—'}</span></div>
                    <div className="account-row"><span className="account-label">Status</span><span className={`account-badge ${statusClass}`}>{status}</span></div>
                    <div className="account-row"><span className="account-label">Seats</span><span className="account-value">{usedSeats} of {capLabel} in use{seatsPurchased > 0 ? ` · ${seatsPurchased} purchased` : ''}</span></div>
                    <div className="account-row"><span className="account-label">Subscription until</span><span className="account-value">{subDate ? fmt(subDate) : (inTrial ? 'Free trial — not yet subscribed' : '—')}</span></div>
                    {inTrial && <div className="account-row"><span className="account-label">Free trial ends</span><span className="account-value">{trialLeft} day{trialLeft === 1 ? '' : 's'} left</span></div>}
                  </div>
                  {seatsPurchased === 0 && orgLicensed && (
                    <div className="account-note warn">⚠ A subscription date is set but no seats are purchased yet. Contact iBetterAgent at {supportLink} to allocate seats so your agents stay covered after the trial window.</div>
                  )}
                </div>
              );
            })()
          ) : effectiveSection === 'message-contacts' && !agent.admin ? (
            <MessageContacts scope={dataScope} agentName={displayName} fileKeys={fileKeys}
              onBack={() => setActiveSection('contacts')} signature={agent.signature || ''} />
          ) : effectiveSection === 'supported-mls' && agent.independent ? (
            <SupportedMls scope={agent.id} />
          ) : effectiveSection === 'reviews' && !agent.admin ? (
            <Reviews scope={dataScope} agentName={displayName} />
          ) : effectiveSection === 'files' ? (
            <FilesLibrary ownerKey={fileOwnerKey} ancestors={fileAncestors}
              subtitle={agent.admin
                ? 'Upload files for your organization. Mark one Public to share it (read-only) with your agents; files your MLS shared with you appear below and can be passed on.'
                : 'Upload your own files and images, and use those your org and MLS have shared with you. Mark one Public to show it on your public profile — documents under Market Reports, images under Charts & Statistics.'}
              {...(!agent.admin ? { profileFiles: agent.profileFiles || [], onToggleProfile: toggleProfileFile } : {})} />
          ) : effectiveSection === 'contacts' ? (
            <Contacts key={dataScope} scope={dataScope} importEnabled={importEnabled}
              onMessageContacts={!agent.admin ? () => setActiveSection('message-contacts') : undefined}
              focusContactId={focusContactId} onFocusHandled={() => setFocusContactId(null)} />
          ) : effectiveSection === 'properties' ? (
            <Properties key={dataScope} scope={dataScope} importEnabled={importEnabled}
              onOpenContact={(id) => { setFocusContactId(id); setActiveSection('contacts'); }} />
          ) : effectiveSection === 'reminder-templates' ? (
            <ReminderTemplates key={agent.admin ? `master-${masterId}` : dataScope}
              scope={agent.admin ? masterId : dataScope}
              isAdmin={!!agent.admin}
              suspendScope={agent.admin ? 'org' : 'self'}
              suspended={agent.admin ? !!(org && org.remindersSuspended) : !!agent.remindersSuspended}
              orgSuspended={!!(org && org.remindersSuspended)}
              onToggleSuspend={agent.admin ? setOrgRemindersSuspended : setMyRemindersSuspended}
              onPush={agent.admin ? pushReminderDown : undefined}
              onPropagateReminder={agent.admin ? (id) => propagateOrgReminder(agent.orgId, id) : undefined}
              fileKeys={fileKeys} />
          ) : effectiveSection === 'reminder-assignments' ? (
            <ReminderAssignments key={dataScope} scope={dataScope} />
          ) : effectiveSection === 'run-reminders' ? (
            <RunReminders key={dataScope} scope={dataScope}
              orgSuspended={!!(org && org.remindersSuspended)}
              selfSuspended={!!agent.remindersSuspended}
              mlsSuspended={['mls', 'system'].includes(processHaltReason(agent, orgs, loadSystemMls(), settings) || '')}
              orgOverCapacity={overCapacity} />
          ) : (
            <>
              <h2>{navItems.find(i => i.id === effectiveSection)?.label}</h2>
              <p>Select an item to get started.</p>
            </>
          )}
        </main>
      </div>

      {/* Native-style mobile bottom navigation (hidden on desktop via CSS). */}
      <nav className="mobile-tabbar">
        {navItems.map(item => (
          <button key={item.id}
            className={`tabbar-item ${effectiveSection === item.id ? 'active' : ''}`}
            onClick={() => goTo(item.id)}>
            <span className="tabbar-icon"><Icon name={item.icon} size={22} /></span>
            <span className="tabbar-label">{TAB_SHORT[item.id] || item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

export default App;
